import {
  AfterViewInit, Component, ElementRef, HostListener,
  OnDestroy, OnInit, QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of, Subscription, timer } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { environment } from '../../environments/environment';
import { MOCK_ESTABELECIMENTO, MOCK_CATEGORIAS, MOCK_FRETE } from './cardapio-mock.data';

import {
  CardapioConsultaDTO,
  CardapioEstabelecimentoDTO,
  CardapioCategoriaDTO,
  CardapioProdutoDTO,
  CardapioItemCarrinhoDTO,
  CardapioItemPedidoDTO,
  CardapioFreteDTO,
  CardapioExigenciasDoPedidoDTO,
  CardapioFormasDePagamentoDTO,
  CardapioEstado,
  CardapioPedidoDTO,
  CardapioCheckoutFormDTO,
  CardapioOpcaoSelecionadaDTO,
  CardapioIdentificacaoDTO,
  CardapioCobrancaQrDTO,
  CardapioEstadoCobrancaQrDTO,
  CardapioTrackingDTO,
  CardapioTrackingItemDTO,
  PedidoHistoricoDTO,
  CardapioClientePerfilDTO,
  PaisDDI,
  CardapioPedidoPublicoDTO,
  CardapioPedidoPublicoItemDTO,
  CardapioOpcaoModificadorPedidoDTO,
} from '../../models/cardapio-digital/cardapio-digital.model';
import { TipoPedido } from '../../enums/TipoPedido.enum';
import { CardapioPublicoService } from '../../services/cardapio-digital/cardapio-publico.service';
import { CardapioWhatsappService } from '../../services/cardapio-digital/cardapio-whatsapp.service';
import { CardapioFreteService } from '../../services/cardapio-digital/cardapio-frete.service';
import { CardapioPedidoService } from '../../services/cardapio-digital/cardapio-pedido.service';
import { CardapioSeoService } from '../../services/cardapio-digital/cardapio-seo.service';
import { CardapioHistoricoService } from '../../services/cardapio-digital/cardapio-historico.service';
import { CardapioClienteCacheService } from '../../services/cardapio-digital/cardapio-cliente-cache.service';
import { ApiService } from '../../services/api.service';
import { StorageService } from '../../services/storage.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { formatarPreco } from './utils/cardapio-preco.util';
import { processarImagensEstabelecimento, processarImagensProdutos } from './utils/cardapio-imagem.util';
import { ajustarCor, ajustarOpacidade, corEhClara } from './utils/cardapio-cor.util';
import { calcularSeAbertoLocal } from './utils/cardapio-horario.util';
import { slugificar, slugificarCategoria } from './utils/cardapio-slug.util';

/**
 * Corpo de erro do backend. `errors` (em ingles) so vem nas falhas de validacao; o restante traz
 * apenas `message`. Ambos os campos sao opcionais — erro de rede nao tem corpo nenhum.
 */
interface RespostaDeErroDaApi {
  message?: string;
  errors?: { campo?: string; mensagem?: string; message?: string }[];
}

@Component({
  selector: 'app-cardapio-digital',
  templateUrl: './cardapio-digital.component.html',
  styleUrls: ['./cardapio-digital.component.scss'],
  providers: [MessageService],
})
export class CardapioDigitalComponent implements OnInit, AfterViewInit, OnDestroy {

  // Estado de navegacao
  estadoAtual: CardapioEstado = 'CARDAPIO';

  // Dados do cardapio
  estabelecimento!: CardapioEstabelecimentoDTO;
  categorias: CardapioCategoriaDTO[] = [];
  freteConfig: CardapioFreteDTO | null = null;

  /** Formas de pagamento aceitas pela loja, vindas de GET /cardapio/{slug}. */
  formasDePagamentoConfig: CardapioFormasDePagamentoDTO | null = null;
  /** O que a loja exige no consumo no local (mesa e/ou comanda). */
  exigenciasDoPedido: CardapioExigenciasDoPedidoDTO | null = null;
  /**
   * Frete calculado por DISTÂNCIA no backend (origem = loja; destino = coords de
   * entrega). Quando definido, sobrepõe o valor fixo do `freteConfig` no preview,
   * para o cliente ver exatamente o que será cobrado ao criar o pedido. `null`
   * quando não é delivery, faltam coordenadas ou o endereço está fora de cobertura.
   */
  freteDistancia: number | null = null;

  // Estado de loading e erro
  loading = true;
  erro: string | null = null;

  // Moeda (padrao BRL ate carregar empresa)
  moedaSimbolo = 'BRL';
  moedaLocal = 'pt-BR';

  // Traducoes carregadas
  texts: any = {};

  // --- Estados visuais estilo Minishop ---

  // Sidebar
  sidebarVisivel = false;

  // Busca (inline no header)
  modoBusca = false;
  termoBusca = '';

  // Falha ao carregar a logo / banner do estabelecimento -> exibe placeholder
  // (mesmo icone de ausencia de imagem dos cards) em vez do texto alt.
  logoComErro = false;
  bannerComErro = false;

  // Cache de produtos filtrados por categoria (evita funcao impura no template)
  produtosFiltradosCache: Map<number, CardapioProdutoDTO[]> = new Map();
  allFilteredCountCache = 0;

  // Categoria ativa no carrossel/scroll spy
  categoriaAtivaId: number | null = null;

  // Botao flutuante "voltar ao topo": fica oculto no topo e aparece quando o
  // usuario rola o host alem do limiar. Controlado pelo @HostListener('scroll').
  mostrarBotaoTopo = false;

  // Limiar (em px de scrollTop do host) a partir do qual o botao de voltar ao
  // topo passa a ser exibido.
  private readonly LIMIAR_BOTAO_TOPO_PX = 350;

  // Menu hamburger de categorias (dropdown)
  menuCategoriaAberto = false;

  // Modal de opcoes (estilo Minishop)
  modalVisivel = false;
  produtoAtual: CardapioProdutoDTO | null = null;

  // Produto selecionado para detalhe (estado DETALHES_PRODUTO)
  produtoSelecionado: CardapioProdutoDTO | null = null;

  // Id da empresa do estabelecimento (usado para montar URLs das imagens secundarias do item)
  idEmpresa = '';

  // Dialog de loja fechada
  lojaFechadaVisivel = false;
  /** Estado do aviso modal unico da tela — ver `cardapio-aviso-dialog`. */
  aviso = { visivel: false, titulo: '', mensagem: '', textoBotao: '' };

  // Modal de checkout (p-dialog)
  checkoutVisivel = false;

  // Dados do pedido para a tela de tracking/acompanhamento
  trackingAtual: CardapioTrackingDTO | null = null;

  /**
   * Cobranca por QR do pedido recem-criado, quando o cliente escolheu cartao ou QR Code.
   *
   * Vive fora do `trackingAtual` porque nao e persistida no historico: o QR expira, e reexibir
   * um QR velho ao reabrir um pedido antigo so induziria o cliente a pagar num codigo morto.
   */
  cobrancaQr: CardapioCobrancaQrDTO | null = null;

  /**
   * Cobranca por QR do checkout — gerada assim que o cliente escolhe QR Code ou cartao, ANTES de
   * existir pedido. Quem cria o pedido e o callback da Bancard, quando o pagamento confirma; a
   * tela so acompanha e avanca sozinha.
   */
  estadoCobrancaQr: CardapioEstadoCobrancaQrDTO = { gerando: false, confirmado: false };
  /** Forma que originou a cobranca em aberto: trocar de forma exige um QR novo. */
  private formaDaCobrancaQr: string | null = null;
  /** Dados do checkout usados na cobranca — reaproveitados quando o pagamento confirma. */
  private formDaCobrancaQr: CardapioCheckoutFormDTO | null = null;
  /** Pedido criado pelo callback assim que o pagamento entrou. */
  private codigoDePedidoDaCobrancaQr: number | null = null;
  private pollingCobrancaQr?: Subscription;

  // Indica se o tracking foi aberto a partir do historico (controla botao de voltar)
  veioDoHistorico = false;

  // Historico de pedidos
  pedidosHistorico: PedidoHistoricoDTO[] = [];
  temHistorico = false;

  // Lista flat de todos os produtos (para sugestoes no carrinho)
  todosOsProdutos: CardapioProdutoDTO[] = [];

  // Tipo de pedido pre-selecionado no carrinho (para passar ao checkout)
  tipoPedidoSelecionado: TipoPedido | null = null;

  // Nome e telefone pre-preenchidos (vindos do modal do carrinho)
  nomeClienteCheckout = '';
  telefoneCheckout = '';
  rucCheckout = '';
  ciCheckout = '';

  // Endereco pre-preenchido (vindo do modal de endereco do carrinho, apenas delivery)
  enderecoCheckout = '';

  // Numero de mesa pre-preenchido (vindo do modal de identificacao do carrinho, apenas MESA)
  numeroMesaCheckout = '';
  numeroComandaCheckout = '';

  // PaisDDI selecionado no carrinho (para salvar perfil no cache)
  private paisDDICheckout: PaisDDI | undefined;

  // Campos de endereco desagrupados (para salvar no cache de perfil)
  private enderecoIdentificacao?: {
    rua: string;
    numero: string;
    complemento?: string;
    referencia?: string;
    latitude?: number;
    longitude?: number;
  };

  // Slug da rota publica (se presente, carrega via endpoint publico)
  slug: string | null = null;

  // Scroll spy
  private intersectionObserver!: IntersectionObserver;
  private isManualScroll = false;
  private manualScrollTimeout: any;
  private dadosCarregados = false;

  // Nome do query param que reflete a categoria selecionada na URL pelo SLUG do
  // nome da categoria, legivel (/menu/:slug?categoria=perfume-femenino).
  private readonly QUERY_PARAM_CATEGORIA = 'categoria';

  // Nome do query param que reflete o PRODUTO aberto na tela de detalhe pelo SLUG
  // do nome do produto (/menu/:slug?categoria=...&item=llaveritos-de-goma).
  // Recarregar a URL com este param reabre a mesma tela de detalhe.
  private readonly QUERY_PARAM_ITEM = 'item';

  // Nome do query param que reflete a "vista" (sub-tela) atual quando ela difere
  // da listagem/detalhe — hoje usado apenas para o CARRINHO/bolsa
  // (/menu/:slug?vista=carrito). Recarregar a URL com este param reabre o carrinho.
  // O valor 'carrito' segue o idioma espanhol do minishop publico.
  private readonly QUERY_PARAM_VISTA = 'vista';
  private readonly VISTA_CARRINHO = 'carrito';

  // Ultimo slug de `item` invalido (presente na URL, mas nao resolvido para um
  // produto existente) que ja gerou o toast "Produto nao disponivel". Evita
  // exibir o mesmo aviso repetidamente caso o slug reapareca antes da limpeza.
  private ultimoItemInvalidoNotificado: string | null = null;

  // Posicao da rolagem da listagem no momento em que o detalhe do produto foi
  // aberto. A listagem sai do DOM no detalhe (*ngIf por estado) e volta no topo;
  // ao fechar, devolvemos o cliente ao ponto em que estava. Null quando o
  // detalhe foi aberto direto pela URL (link compartilhado).
  private scrollDaListagemAntesDoDetalhe: number | null = null;

  @ViewChildren('catAnchor') catAnchors!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('hamburgerWrapper') hamburgerWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('navTabs') navTabs!: ElementRef<HTMLDivElement>;

  // O arraste horizontal das abas de categoria (drag-to-scroll), a conversao da
  // roda vertical em rolagem lateral e o estado "ha conteudo escondido a
  // esquerda/direita" ficam na diretiva `appDragScroll`, aplicada a .nav-tabs no
  // template — ver src/app/directives/drag-scroll.directive.ts. Ela tambem
  // suprime o clique disparado logo apos um arraste, entao selectCategoria nao
  // precisa distinguir clique de arraste.

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private messageService: MessageService,
    private elementRef: ElementRef,
    private cardapioPublicoService: CardapioPublicoService,
    private whatsappService: CardapioWhatsappService,
    private freteService: CardapioFreteService,
    public pedidoService: CardapioPedidoService,
    private apiService: ApiService,
    private storageService: StorageService,
    private empresaConfigService: EmpresaConfigService,
    private seoService: CardapioSeoService,
    private historicoService: CardapioHistoricoService,
    private clienteCacheService: CardapioClienteCacheService,
  ) {}

  /** Armazena o background original do body para restaurar no ngOnDestroy */
  private bodyBgOriginal = '';

  ngOnInit(): void {
    // Habilitar scroll nativo no body (sem scrollbar visivel em mobile)
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    // Esconder scrollbar mantendo scroll funcional (mobile)
    document.documentElement.style.scrollbarWidth = 'none';
    document.body.style.scrollbarWidth = 'none';
    document.documentElement.classList.add('cardapio-digital-active');
    document.body.classList.add('cardapio-digital-active');

    // Salvar e sobrescrever o background do body para o cardapio (identico a referencia)
    this.bodyBgOriginal = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f7f7fb';

    this.loadTranslations();

    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.loadTranslations())
    );

    // Detectar slug da rota publica /menu/:slug
    this.slug = this.route.snapshot.paramMap.get('slug');

    // Isolar o carrinho por shop: cada shop tem sua propria sacola.
    this.pedidoService.definirShop(this.slug);

    // Carregar historico de pedidos (localStorage)
    const slugHistorico = this.slug || 'preview';
    this.historicoService.carregarPorSlug(slugHistorico);

    // Assinar o observable para manter reatividade quando novos pedidos forem adicionados
    this.subs.push(
      this.historicoService.historicoObservable.subscribe(pedidos => {
        this.pedidosHistorico = pedidos;
        this.temHistorico = pedidos.length > 0;
      })
    );

    // Restaurar tracking ativo apos refresh da pagina (se houver pedido pendente no localStorage)
    this.restaurarTrackingAposRefresh(slugHistorico);

    if (this.slug) {
      this.carregarCardapioPorSlug(this.slug);
    } else {
      this.carregarCardapioPreview();
    }

    // Reagir a mudancas do query param `categoria` sem recriar o componente
    // (ex.: botoes voltar/avancar do navegador ou link compartilhado). O valor do
    // param e o SLUG do nome da categoria; resolvemos a categoria correspondente
    // comparando esse slug com o slug derivado do nome de cada categoria. Como o
    // clique ja atualiza categoriaAtivaId antes de reescrever a URL, comparamos
    // com o id ativo para nao re-disparar o scroll (evita ciclo/jitter).
    this.subs.push(
      this.route.queryParamMap.subscribe((params) => {
        if (!this.dadosCarregados) {
          return;
        }

        const slugCategoria = params.get(this.QUERY_PARAM_CATEGORIA);
        if (slugCategoria) {
          const categoria = this.resolverCategoriaPorSlug(slugCategoria);
          if (categoria && categoria.id !== this.categoriaAtivaId) {
            this.navegarParaCategoria(categoria.id, { atualizarUrl: false });
          }
        }

        // Precedencia: `vista=carrito` tem prioridade sobre `item`. Sincronizamos
        // a vista do carrinho ANTES do detalhe; quando o carrinho esta ativo na
        // URL, NAO sincronizamos o detalhe (o caso comum e so um por vez).
        const vista = params.get(this.QUERY_PARAM_VISTA);
        this.sincronizarVistaComUrl(vista);

        if (vista !== this.VISTA_CARRINHO) {
          // Sincronizar a tela de detalhe do produto com o query param `item`.
          // Permite que os botoes voltar/avancar do navegador abram/fechem o
          // detalhe sem reescrever a URL (evita ciclo). O valor do param e o SLUG
          // do nome do produto; quando a categoria tambem esta presente, usamos
          // como desambiguador para nomes repetidos.
          const slugItem = params.get(this.QUERY_PARAM_ITEM);
          this.sincronizarDetalheComUrl(slugItem, slugCategoria);
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.catAnchors?.changes.subscribe(() => {
      if (!this.loading && !this.intersectionObserver) {
        this.setupIntersectionObserver();
      }
      this.atualizarAlturaHeader();
    });
  }

  // --- Fechar dropdown ao clicar fora ---
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.menuCategoriaAberto && this.hamburgerWrapper) {
      const clickedInside = this.hamburgerWrapper.nativeElement.contains(event.target as Node);
      if (!clickedInside) {
        this.menuCategoriaAberto = false;
      }
    }
  }

  // A barra de busca/categorias fica fixa no topo via CSS (position: sticky).
  // Nao e necessario listener de scroll para isso.

  /**
   * Atualiza a visibilidade do botao flutuante "voltar ao topo" conforme a
   * posicao do scroll. Como o proprio :host e o container de scroll desta tela
   * (100dvh, overflow-y: auto), o @HostListener('scroll') captura o scroll do
   * host e lemos a posicao por `nativeElement.scrollTop`. A operacao e barata
   * (apenas leitura do scrollTop + comparacao), entao nao precisa de throttle.
   */
  @HostListener('scroll')
  onHostScroll(): void {
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    this.mostrarBotaoTopo = hostEl.scrollTop > this.LIMIAR_BOTAO_TOPO_PX;
    // Atualiza a categoria ativa (secao mais proxima do topo) ao rolar a lista.
    this.atualizarCategoriaAtivaPorScroll();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.atualizarAlturaHeader();
  }

  /**
   * Mede a altura real do header sticky (faixa de marca + nav) e expoe em
   * `--cardapio-header-h`, usada no scroll-margin-top das secoes para que o
   * scrollIntoView pare LOGO ABAIXO do header completo (e nao atras dele).
   */
  private atualizarAlturaHeader(): void {
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    const header = hostEl.querySelector('.header-sticky') as HTMLElement | null;
    if (header) {
      hostEl.style.setProperty('--cardapio-header-h', `${header.offsetHeight}px`);
    }
  }

  /**
   * Indica se ha itens no carrinho — usado pelo template para elevar o botao
   * flutuante acima da barra inferior do carrinho (footer-pedido), que so e
   * exibida quando ha itens (quantidade > 0).
   */
  get temItensNoCarrinho(): boolean {
    return this.pedidoService.getQuantidadeTotal() > 0;
  }

  /**
   * Texto de tempo de entrega para o chip de info rapida do cabecalho
   * (ex.: "30–45 min"). Retorna null quando o estabelecimento nao informa.
   */
  get tempoEntregaTexto(): string | null {
    const min = this.estabelecimento?.tempoEntregaMinimo;
    const max = this.estabelecimento?.tempoEntregaMaximo;
    if (min == null && max == null) return null;
    if (min != null && max != null) return `${min}–${max} min`;
    return `${min ?? max} min`;
  }

  // IDs das secoes virtuais de destaque na listagem. Negativos para nao colidir
  // com ids reais de categoria — o observer/scroll usam `cat-${id}` (ex.: cat--1).
  readonly SECAO_PROMOCAO = -1;
  readonly SECAO_NOVIDADE = -2;

  /** Indica se ha ao menos um produto em promocao (vigente). */
  get temProdutosPromocao(): boolean {
    return this.todosOsProdutos.some(p => p.promocao);
  }

  /** Indica se ha ao menos um produto novidade (vigente). */
  get temProdutosNovidade(): boolean {
    return this.todosOsProdutos.some(p => p.novidade);
  }

  /** Produtos da secao virtual "Promocoes" (flat). */
  produtosPromocao(): CardapioProdutoDTO[] {
    return this.todosOsProdutos.filter(p => p.promocao);
  }

  /** Produtos da secao virtual "Novidades" (flat). */
  produtosNovidade(): CardapioProdutoDTO[] {
    return this.todosOsProdutos.filter(p => p.novidade);
  }

  /**
   * ID da PRIMEIRA secao da listagem: a virtual de destaque quando existe
   * (promocao tem precedencia, depois novidade), senao a primeira categoria.
   * Usado para destacar a aba correta quando a lista esta no topo.
   */
  get primeiraSecaoId(): number {
    if (this.temProdutosPromocao) return this.SECAO_PROMOCAO;
    if (this.temProdutosNovidade) return this.SECAO_NOVIDADE;
    return this.categorias[0]?.id ?? 0;
  }

  /**
   * Rola suavemente o container (host) de volta ao topo. Acao do botao
   * flutuante "voltar ao topo". Reutiliza o helper interno scrollHostAoTopo.
   */
  voltarAoTopo(): void {
    this.scrollHostAoTopo(true);
  }

  // --- Setup do IntersectionObserver (estilo Minishop) ---
  private setupIntersectionObserver(): void {
    if (!this.dadosCarregados || this.categorias.length === 0) return;

    // O ancestral scrollavel agora e o proprio host (100dvh, overflow-y: auto),
    // pois o body esta travado (overflow: hidden em styles.scss). Usar o host
    // como `root` garante que os thresholds de interseccao sejam calculados em
    // relacao ao container que realmente rola, mantendo o destaque da aba de
    // categoria ativa preciso durante o scroll.
    const hostEl = this.elementRef.nativeElement as HTMLElement;

    // Offset superior = altura da barra sticky (.cardapio-nav-bar) + folga,
    // identico ao scroll-margin-top aplicado nas secoes no SCSS. Encolhe a
    // "area de deteccao" do observer no topo para que a linha que define a
    // categoria ativa fique LOGO ABAIXO da barra (e nao atras dela). Assim a
    // aba destacada corresponde a secao realmente visivel abaixo da barra.
    const navBar = hostEl.querySelector('.cardapio-nav-bar') as HTMLElement | null;
    const offsetTopo = (navBar?.offsetHeight ?? 45) + 12;
    const options: IntersectionObserverInit = {
      root: hostEl,
      rootMargin: `-${offsetTopo}px 0px -80% 0px`,
      threshold: 0,
    };

    // Usa o mesmo calculo deterministico do scroll (secao mais proxima do topo),
    // mantendo a categoria ativa correta tambem para secoes altas.
    this.intersectionObserver = new IntersectionObserver(() => {
      this.atualizarCategoriaAtivaPorScroll();
    }, options);

    this.catAnchors?.forEach(anchor => {
      const sectionEl = anchor.nativeElement.closest('section');
      if (sectionEl && sectionEl.id) {
        this.intersectionObserver.observe(sectionEl);
      }
    });
  }

  /**
   * Define a categoria ativa como a SECAO mais proxima do topo da area de
   * exibicao (logo abaixo da barra de categorias). Funciona em scroll para
   * cima e para baixo, inclusive com secoes altas. So roda no estado CARDAPIO.
   */
  private atualizarCategoriaAtivaPorScroll(): void {
    if (this.isManualScroll || this.estadoAtual !== 'CARDAPIO') return;

    const hostEl = this.elementRef.nativeElement as HTMLElement;
    // Header completo (faixa de marca + nav) gruda no topo.
    const header = hostEl.querySelector('.header-sticky') as HTMLElement | null;
    const headerH = header?.offsetHeight ?? 130;
    const linhaY = hostEl.getBoundingClientRect().top + headerH + 8;

    // Secao ativa = a que tem o topo acima (ou na) linha de deteccao e mais
    // proxima dela (a secao em que o scroll esta dentro).
    let melhorId: number | null = null;
    let melhorTop = -Infinity;
    hostEl.querySelectorAll('section[id^="cat-"]').forEach((sec) => {
      const top = (sec as HTMLElement).getBoundingClientRect().top;
      if (top <= linhaY + 1 && top > melhorTop) {
        melhorTop = top;
        melhorId = Number((sec as HTMLElement).id.replace('cat-', ''));
      }
    });

    if (melhorId === null) melhorId = this.primeiraSecaoId;

    if (this.categoriaAtivaId !== melhorId) {
      this.categoriaAtivaId = melhorId;
      // A URL acompanha a categoria visivel (replaceUrl: nao enche o historico).
      if (melhorId !== null) {
        this.atualizarUrlCategoria(melhorId);
      }
      // Adia ate o Angular aplicar a classe .ativo na NOVA aba, senao a barra
      // rolaria com base na aba antiga (no scroll para cima nao voltava p/ esq).
      setTimeout(() => this.scrollTabAtivaParaVisivelSeNecessario());
    }
  }

  /**
   * Rola a barra horizontal de categorias APENAS quando a aba ativa esta fora
   * da area visivel, trazendo-a para dentro (sem centralizar). Opera somente
   * sobre o scrollLeft do container das abas (nao afeta o scroll da pagina).
   */
  private scrollTabAtivaParaVisivelSeNecessario(): void {
    const container = this.navTabs?.nativeElement;
    if (!container) return;

    const ativo = container.querySelector('.nav-tab.ativo') as HTMLElement | null;
    if (!ativo) {
      // Categoria ativa e uma secao virtual (Promocoes/Novidades), fora da barra
      // rolavel. Volta a barra ao inicio para mostrar as primeiras categorias.
      container.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    // Usa getBoundingClientRect + scrollBy (robusto independente do offsetParent).
    const contRect = container.getBoundingClientRect();
    const tabRect = ativo.getBoundingClientRect();

    if (tabRect.left < contRect.left) {
      container.scrollBy({ left: tabRect.left - contRect.left - 12, behavior: 'smooth' });
    } else if (tabRect.right > contRect.right) {
      container.scrollBy({ left: tabRect.right - contRect.right + 12, behavior: 'smooth' });
    }
  }

  // --- Menu hamburger de categorias ---
  toggleMenuCategoria(): void {
    this.menuCategoriaAberto = !this.menuCategoriaAberto;
  }

  selecionarCategoriaMenu(id: number): void {
    this.menuCategoriaAberto = false;
    this.selectCategoria(id);
  }

  // --- Selecao de categoria no carrossel ---
  // Disparado pelo clique do usuario na aba/menu: navega visualmente E reflete na URL.
  // O clique disparado logo apos um arraste da barra nem chega aqui: a diretiva
  // `appDragScroll` o suprime na fase de captura.
  selectCategoria(id: number): void {
    // Quando acionado a partir da tela de detalhe, volta ao cardapio antes de
    // navegar — a secao da categoria (alvo do scroll) so existe no DOM do
    // estado CARDAPIO. O setTimeout aguarda o render do cardapio.
    if (this.estadoAtual === 'DETALHES_PRODUTO') {
      this.fecharDetalheProduto({ atualizarUrl: true });
      setTimeout(() => this.navegarParaCategoria(id, { atualizarUrl: true }), 0);
      return;
    }
    this.navegarParaCategoria(id, { atualizarUrl: true });
  }

  /**
   * Navegacao visual ate uma categoria: destaca a aba ativa e rola a pagina ate
   * a secao correspondente (com o mesmo offset/scroll-margin-top do SCSS).
   *
   * @param id ID da categoria de destino.
   * @param opcoes.atualizarUrl quando true, reflete a categoria no query param
   *   `categoria` da URL sem recarregar a pagina nem poluir o historico. Quando
   *   false (ex.: aplicacao a partir da propria URL no load), NAO reescreve a URL,
   *   evitando ciclos entre escrita e leitura do query param.
   */
  private navegarParaCategoria(id: number, opcoes: { atualizarUrl: boolean }): void {
    if (this.manualScrollTimeout) {
      clearTimeout(this.manualScrollTimeout);
    }

    this.isManualScroll = true;
    this.categoriaAtivaId = id;

    if (opcoes.atualizarUrl) {
      this.atualizarUrlCategoria(id);
    }

    const el = document.getElementById(`cat-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Mantem a aba selecionada visivel na barra horizontal (se estiver fora).
    setTimeout(() => this.scrollTabAtivaParaVisivelSeNecessario());

    this.manualScrollTimeout = setTimeout(() => {
      this.isManualScroll = false;
    }, 700);
  }

  /**
   * Reflete a categoria selecionada no query param `categoria` da URL, gravando
   * o SLUG do NOME da categoria (legivel) em vez do id.
   *
   * Usa `replaceUrl: true` para NAO empilhar uma entrada no historico do
   * navegador a cada clique (evita que o botao "voltar" precise passar por todas
   * as categorias clicadas). `queryParamsHandling: 'merge'` preserva eventuais
   * outros query params. Como apenas o query param muda (mesma rota), o Angular
   * NAO recria o componente nem recarrega os dados.
   *
   * Se a categoria nao for encontrada ou o nome gerar slug vazio, a URL nao e
   * alterada (comportamento gracioso).
   */
  private atualizarUrlCategoria(id: number): void {
    const categoria = this.categorias.find(c => c.id === id);
    const slugCategoria = slugificarCategoria(categoria?.nome);
    if (!slugCategoria) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.QUERY_PARAM_CATEGORIA]: slugCategoria },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Resolve a categoria do catalogo cujo slug do nome corresponde ao slug
   * informado (vindo do query param da URL).
   *
   * Colisao: se duas categorias gerarem o mesmo slug (nomes iguais/parecidos),
   * retorna a de MENOR `ordem` (ou, em empate, a primeira na lista atual) — de
   * forma graciosa, sem quebrar.
   *
   * @param slugCategoria Slug vindo da URL (ex.: "perfume-femenino").
   * @returns A categoria correspondente ou `undefined` se nenhuma casar.
   */
  private resolverCategoriaPorSlug(slugCategoria: string): CardapioCategoriaDTO | undefined {
    if (!slugCategoria) {
      return undefined;
    }
    const correspondentes = this.categorias.filter(
      c => slugificarCategoria(c.nome) === slugCategoria
    );
    if (correspondentes.length === 0) {
      return undefined;
    }
    return correspondentes.reduce((menor, atual) =>
      (atual.ordem ?? Number.MAX_SAFE_INTEGER) < (menor.ordem ?? Number.MAX_SAFE_INTEGER)
        ? atual
        : menor
    );
  }

  /**
   * Resolve o produto do catalogo cujo slug do nome corresponde ao slug
   * informado (vindo do query param `item` da URL).
   *
   * Desambiguacao por categoria: quando `slugCategoria` e informado e ha mais de
   * um produto com o mesmo slug, prioriza o que pertence a categoria
   * correspondente. Caso contrario, retorna a primeira correspondencia global
   * (graciosa). Slug vazio/inexistente => `undefined`.
   *
   * @param slugItem Slug do nome do produto vindo da URL (ex.: "llaveritos-de-goma").
   * @param slugCategoria Slug da categoria (opcional) para desambiguar nomes repetidos.
   * @returns O produto correspondente ou `undefined` se nenhum casar.
   */
  private resolverProdutoPorSlug(
    slugItem: string,
    slugCategoria: string | null
  ): CardapioProdutoDTO | undefined {
    if (!slugItem) {
      return undefined;
    }

    // Quando a categoria esta presente na URL, tentar primeiro dentro dela para
    // desambiguar nomes de produto repetidos entre categorias diferentes.
    if (slugCategoria) {
      const categoria = this.resolverCategoriaPorSlug(slugCategoria);
      const naCategoria = categoria?.produtos.find(
        p => slugificar(p.nome) === slugItem
      );
      if (naCategoria) {
        return naCategoria;
      }
    }

    // Busca global: primeira correspondencia pelo slug do nome.
    return this.todosOsProdutos.find(p => slugificar(p.nome) === slugItem);
  }

  /**
   * Localiza a categoria REAL a que pertence um produto, percorrendo a estrutura
   * categorias[].produtos[] por id (o produto nao expoe um campo direto de
   * categoria). E a fonte unica de verdade usada tanto para reescrever o query
   * param `categoria` ao abrir o detalhe quanto para montar o link de
   * compartilhamento do card, garantindo que ambos derivem a categoria da mesma
   * forma.
   *
   * @param produto Produto cuja categoria se deseja descobrir.
   * @returns A categoria que contem o produto ou `undefined` se nao encontrada.
   */
  private categoriaDoProduto(produto: CardapioProdutoDTO): CardapioCategoriaDTO | undefined {
    return this.categorias.find(c => c.produtos.some(p => p.id === produto.id));
  }

  /**
   * Nome da categoria do produto atualmente aberto na tela de detalhe. Como o
   * produto do payload publico nao expoe a categoria diretamente, derivamos a
   * categoria real percorrendo categorias[].produtos[] (mesma fonte usada para o
   * link compartilhado). Retorna null quando nao ha produto selecionado ou a
   * categoria nao for encontrada, permitindo que o template oculte a linha.
   */
  get categoriaNomeDetalhe(): string | null {
    if (!this.produtoSelecionado) {
      return null;
    }
    return this.categoriaDoProduto(this.produtoSelecionado)?.nome ?? null;
  }

  /**
   * Sincroniza a tela de detalhe do produto com o query param `item` da URL.
   *
   * - Se ha `item` e resolve um produto: abre o detalhe (sem reescrever a URL).
   * - Se ha `item` mas nao resolve (slug invalido/inexistente): ignora
   *   graciosamente (permanece na listagem), porem fecha um detalhe aberto que
   *   nao corresponda mais ao param.
   * - Se nao ha `item` e o detalhe esta aberto: fecha o detalhe (sem reescrever
   *   a URL) — cobre o botao "voltar" do navegador.
   *
   * Nunca reescreve a URL (atualizarUrl: false) para nao entrar em ciclo com a
   * leitura do proprio query param.
   *
   * @param slugItem Valor do param `item` (ou null se ausente).
   * @param slugCategoria Valor do param `categoria` (desambiguador), ou null.
   */
  private sincronizarDetalheComUrl(
    slugItem: string | null,
    slugCategoria: string | null
  ): void {
    if (!slugItem) {
      // URL sem `item`: garantir que o detalhe esteja fechado (ex.: voltar do navegador).
      if (this.estadoAtual === 'DETALHES_PRODUTO') {
        this.fecharDetalheProduto({ atualizarUrl: false });
      }
      return;
    }

    const produto = this.resolverProdutoPorSlug(slugItem, slugCategoria);
    if (!produto) {
      // Chegamos aqui APENAS quando ha um `item={slug}` REALMENTE presente na URL
      // (o ramo `if (!slugItem)` acima ja tratou a ausencia do param) que NAO
      // resolveu para nenhum produto do catalogo. Isso ocorre quando o produto
      // esta sem estoque (omitido pelo backend) ou o slug e invalido/inexistente.
      // Em vez de ignorar silenciosamente, avisamos o usuario e limpamos a URL.

      // Slug invalido/inexistente: se um detalhe estiver aberto e nao corresponder
      // ao param atual, fechar para manter consistencia.
      if (
        this.estadoAtual === 'DETALHES_PRODUTO' &&
        slugificar(this.produtoSelecionado?.nome) !== slugItem
      ) {
        this.fecharDetalheProduto({ atualizarUrl: false });
      }

      // Exibir o toast neutro uma unica vez por slug invalido (evita repeticao
      // caso o mesmo slug reapareca antes da limpeza da URL).
      if (this.ultimoItemInvalidoNotificado !== slugItem) {
        this.ultimoItemInvalidoNotificado = slugItem;
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('cardapio_digital.produto_indisponivel.titulo'),
          detail: this.translate.instant('cardapio_digital.produto_indisponivel.detalhe'),
          life: 4000,
        });
      }

      // Remover o param `item` invalido da URL (preservando `categoria`). Isso
      // dispara novamente o subscribe de `queryParamMap`, mas agora com `item`
      // ausente: o fluxo cai no ramo `if (!slugItem)` acima e NAO reexibe o toast,
      // evitando loop. A flag acima e zerada ao limpar a URL com sucesso.
      this.removerUrlItem();
      this.ultimoItemInvalidoNotificado = null;
      return;
    }

    // Ja exibindo este mesmo produto: nada a fazer (evita re-render/scroll).
    if (
      this.estadoAtual === 'DETALHES_PRODUTO' &&
      this.produtoSelecionado?.id === produto.id
    ) {
      return;
    }

    this.abrirDetalheProduto(produto, { atualizarUrl: false });
  }

  /**
   * Aplica a categoria informada no query param `categoria` (se houver) apos os
   * dados/secoes estarem renderizados. O valor do param e o SLUG do nome da
   * categoria; resolvemos a categoria correspondente por esse slug. Ignora
   * graciosamente valores ausentes ou que nao correspondem a nenhuma categoria
   * do catalogo carregado (slug invalido/inexistente => comportamento padrao,
   * sem erro).
   *
   * Importante: NAO reescreve a URL (atualizarUrl: false) para nao entrar em
   * ciclo com a leitura do proprio query param.
   *
   * Tambem aplica o param `item` (se houver), abrindo a tela de detalhe do
   * produto correspondente apos os produtos estarem carregados. Tudo dentro de
   * um unico setTimeout para garantir que catalogo/secoes ja existam no DOM,
   * seguindo o mesmo padrao usado no setupIntersectionObserver.
   */
  private aplicarParamsDaUrl(): void {
    if (this.categorias.length === 0) {
      return;
    }

    const params = this.route.snapshot.queryParamMap;
    const slugCategoria = params.get(this.QUERY_PARAM_CATEGORIA);
    const slugItem = params.get(this.QUERY_PARAM_ITEM);
    const vista = params.get(this.QUERY_PARAM_VISTA);

    if (!slugCategoria && !slugItem && !vista) {
      return;
    }

    // As secoes/produtos sao renderizados de forma assincrona apos o
    // carregamento dos dados. Aguardar o proximo ciclo garante que o catalogo ja
    // exista no DOM (e que `todosOsProdutos` esteja populado).
    setTimeout(() => {
      if (slugCategoria) {
        const categoria = this.resolverCategoriaPorSlug(slugCategoria);
        if (categoria) {
          // A secao continua identificada internamente por id (#cat-{id});
          // apenas o PARAMETRO da URL passa a ser o slug do nome.
          this.navegarParaCategoria(categoria.id, { atualizarUrl: false });
        }
      }

      // Precedencia: `vista=carrito` tem prioridade sobre `item`. Se a URL pede o
      // carrinho, abrimos o carrinho e NAO abrimos o detalhe (caso atipico de
      // ambos presentes). Caso contrario, sincronizamos o detalhe normalmente.
      if (vista === this.VISTA_CARRINHO) {
        this.sincronizarVistaComUrl(vista);
      } else {
        // Abrir o detalhe do produto SOMENTE depois que os produtos estao
        // carregados. Slug invalido/inexistente e ignorado graciosamente.
        this.sincronizarDetalheComUrl(slugItem, slugCategoria);
      }
    });
  }

  // --- Carregar dados ---

  /**
   * Carrega o cardapio usando o endpoint PUBLICO GET /api/cardapio/{slug}.
   * Usa CardapioPublicoService que bypassa interceptors de autenticacao.
   */
  private carregarCardapioPorSlug(slug: string): void {
    this.loading = true;
    this.erro = null;

    this.cardapioPublicoService.buscarCardapioPorSlug(slug).subscribe({
      next: (response: CardapioConsultaDTO) => {
        this.processarResposta(response);
      },
      error: (err: unknown) => {
        if (this.ehLojaIndisponivel(err)) {
          this.marcarLojaIndisponivel();
        } else {
          this.erro = this.translate.instant('cardapio_digital.erro_carregar');
        }
        this.loading = false;
      },
    });
  }

  /** O backend responde 403 em todo endpoint publico da loja quando o MiniShop esta desabilitado. */
  private ehLojaIndisponivel(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 403;
  }

  /** Troca o cardapio pela tela de indisponivel (o template esconde tudo quando ha `erro`). */
  onLojaIndisponivel(): void {
    this.marcarLojaIndisponivel();
  }

  private marcarLojaIndisponivel(): void {
    this.pararPollingCobrancaQr();
    this.erro = this.translate.instant('cardapio_digital.loja_indisponivel');
  }

  /**
   * 422 de loja fechada / fora do horario (`Estabelecimento_fechado` / `Estabelecimento_fora_do_horario`
   * no backend, pt-BR e es). Os outros 422 sao regras de negocio do pedido e nao significam loja fechada.
   */
  private ehLojaFechada(err: HttpErrorResponse, motivo: string): boolean {
    const texto = `${(err.error as RespostaDeErroDaApi | null | undefined)?.message ?? ''} ${motivo}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return /estabelecimento esta fechado|estabelecimento esta fora do horario|establecimiento esta cerrado|establecimiento esta fuera del horario/.test(texto);
  }

  private carregarCardapioPreview(): void {
    this.loading = true;
    this.erro = null;

    this.apiService.read<CardapioConsultaDTO>('/cardapio/preview').subscribe({
      next: (response: CardapioConsultaDTO) => {
        this.processarResposta(response);
      },
      error: () => {
        this.carregarDadosMock();
      },
    });
  }

  /**
   * Verifica se o produto possui um preco valido para exibicao publica.
   *
   * Regra: o produto e considerado "com preco" se:
   *  - `preco > 0`, OU
   *  - `menorPrecoVariante > 0` (indicador agregado do backend), OU
   *  - alguma variante em `variantes` possui `preco > 0`.
   *
   * Produtos com preco zero/negativo nao devem aparecer no cardapio publico.
   *
   * Observacao: o contrato GET /api/cardapio/{slug} (ver
   * vendas/docs/api/API_CONTRACT_cardapio_digital.md) NAO expoe campos de
   * estoque (quantidadeEstoque/controlaEstoque). Por isso, o filtro de
   * estoque nao pode ser feito aqui no frontend e deve ser aplicado no
   * backend (CardapioViewMapper) caso seja necessario.
   */
  private produtoTemPrecoValido(produto: CardapioProdutoDTO): boolean {
    if (produto.preco != null && produto.preco > 0) {
      return true;
    }
    if (produto.menorPrecoVariante != null && produto.menorPrecoVariante > 0) {
      return true;
    }
    if (produto.variantes && produto.variantes.some(v => v.preco > 0)) {
      return true;
    }
    return false;
  }

  /**
   * Remove produtos sem preco valido, ordena os produtos de cada categoria em
   * ordem alfabetica pelo nome, ordena as proprias categorias em ordem
   * alfabetica pelo nome e descarta categorias que ficarem vazias.
   * Retorna uma nova lista (nao muta o array original do response).
   *
   * A ordenacao usa localeCompare com sensitivity 'base', tornando-a
   * case-insensitive e acento-insensitive (ex.: "Acai" == "ACAI" == "açaí").
   *
   * A mesma lista ordenada alimenta a barra de navegacao/abas, o menu hamburger
   * e as secoes da listagem (todos iteram sobre `this.categorias`), garantindo
   * ordem consistente. Como a navegacao por ancora usa o `id` da categoria
   * (e nao o indice), reordenar nao afeta scroll/ancora.
   */
  private filtrarCategoriasVisiveis(
    categorias: CardapioCategoriaDTO[]
  ): CardapioCategoriaDTO[] {
    return categorias
      .map(cat => ({
        ...cat,
        produtos: (cat.produtos || [])
          .filter(p => this.produtoTemPrecoValido(p))
          .sort((a, b) =>
            (a.nome || '').localeCompare(b.nome || '', undefined, {
              sensitivity: 'base',
            })
          ),
      }))
      .filter(cat => cat.produtos.length > 0)
      .sort((a, b) =>
        (a.nome || '').localeCompare(b.nome || '', undefined, {
          sensitivity: 'base',
        })
      );
  }

  /**
   * Processa a resposta do cardapio (usado tanto pelo endpoint publico quanto pelo preview).
   * Constroi URLs de imagens, aplica tema e inicializa scroll spy.
   */
  private processarResposta(response: CardapioConsultaDTO): void {
    this.estabelecimento = response.estabelecimento;
    // Filtrar produtos sem preco valido e categorias vazias antes de qualquer
    // processamento (imagens, cache, scroll spy). Itens com preco 0 nao devem
    // aparecer no cardapio publico para o consumidor final.
    this.categorias = this.filtrarCategoriasVisiveis(response.categorias || []);
    this.freteConfig = response.frete || null;
    this.formasDePagamentoConfig = response.formasDePagamento || null;
    this.exigenciasDoPedido = response.exigenciasDoPedido || null;

    // Recalcular status aberto usando o fuso do estabelecimento (America/Asuncion).
    // O campo `aberto` do backend pode estar desatualizado; calcularSeAbertoLocal usa Intl.DateTimeFormat.
    if (response.estabelecimento.horarioFuncionamento) {
      const abertoLocal = calcularSeAbertoLocal(response.estabelecimento.horarioFuncionamento);
      // Flag manual do operador veta "aberto" mesmo que o horario indique abertura
      response.estabelecimento.aberto = response.estabelecimento.aberto && abertoLocal;
    }

    // Construir prefixos de imagem usando o empresaId retornado pelo backend.
    // O empresaId vem no DTO do estabelecimento, garantindo que funciona tanto
    // na rota publica (slug) quanto no preview (autenticado).
    const idEmpresa = this.estabelecimento.empresaId?.toString()
      || this.storageService.getItem('id') || '';
    // Guarda para o componente de detalhe montar as URLs das imagens secundarias do item.
    this.idEmpresa = idEmpresa;
    const prefixoEstabelecimento = `${environment.imgHost}/${idEmpresa}/configuracao_empresa`;
    const prefixoProduto = `${environment.imgHost}/${idEmpresa}/item`;

    // Construir URLs completas das imagens (logica extraida para utils)
    processarImagensEstabelecimento(this.estabelecimento, prefixoEstabelecimento);
    processarImagensProdutos(this.categorias, prefixoProduto);

    // Fallback para banner (apenas no modo preview autenticado)
    if (!this.slug && !this.estabelecimento.banner && !this.estabelecimento.imagemDeTitulo) {
      this.carregarBannerMiniShop(idEmpresa);
    }

    // Aplicar tema de cores
    this.aplicarTemaEstabelecimento();

    // Configurar moeda do estabelecimento (se retornada pelo backend)
    if (this.estabelecimento.moeda) {
      this.moedaSimbolo = this.estabelecimento.moeda.codigo;
      this.moedaLocal = this.estabelecimento.moeda.locale;
    }

    // Definir categoria ativa inicial
    if (this.categorias.length > 0) {
      this.categoriaAtivaId = this.categorias[0].id;
    }

    // Inicializar cache de produtos filtrados
    this.atualizarCacheFiltrados();

    // Montar lista flat de todos os produtos (para sugestoes no carrinho)
    this.todosOsProdutos = this.categorias.flatMap(c => c.produtos);

    this.loading = false;
    this.dadosCarregados = true;

    // Exibir dialog de loja fechada automaticamente no acesso publico (slug presente)
    if (this.slug && !this.estabelecimento.aberto) {
      this.lojaFechadaVisivel = true;
    }

    // Aplicar SEO (Schema.org, Open Graph, PWA) apenas na rota publica
    this.seoService.aplicar(this.estabelecimento, this.categorias, this.moedaSimbolo, this.slug);

    setTimeout(() => { this.setupIntersectionObserver(); this.atualizarAlturaHeader(); });

    // Se a URL ja vier com ?categoria=... e/ou ?item=..., aplicar automaticamente.
    this.aplicarParamsDaUrl();
  }

  /**
   * Carrega dados mock de teste para quando o backend nao esta disponivel.
   * Somente usado em ambiente de desenvolvimento.
   */
  private carregarDadosMock(): void {
    if (environment.production) {
      this.erro = this.translate.instant('cardapio_digital.erro_carregar');
      this.loading = false;
      return;
    }

    this.estabelecimento = { ...MOCK_ESTABELECIMENTO };
    // Aplicar o mesmo filtro do fluxo real: produtos sem preco e categorias
    // vazias nao devem ser exibidos para o consumidor final.
    this.categorias = this.filtrarCategoriasVisiveis(
      MOCK_CATEGORIAS.map(c => ({ ...c, produtos: c.produtos.map(p => ({ ...p })) }))
    );
    this.freteConfig = { ...MOCK_FRETE };

    this.aplicarTemaEstabelecimento();

    if (this.categorias.length > 0) {
      this.categoriaAtivaId = this.categorias[0].id;
    }

    // Inicializar cache de produtos filtrados
    this.atualizarCacheFiltrados();

    // Montar lista flat de todos os produtos (para sugestoes no carrinho)
    this.todosOsProdutos = this.categorias.flatMap(c => c.produtos);

    this.loading = false;
    this.dadosCarregados = true;

    setTimeout(() => { this.setupIntersectionObserver(); this.atualizarAlturaHeader(); });

    // Se a URL ja vier com ?categoria=... e/ou ?item=..., aplicar automaticamente.
    this.aplicarParamsDaUrl();
  }

  /**
   * Rola o container de scroll da pagina ao topo.
   *
   * Com o body travado (overflow: hidden em styles.scss quando
   * .cardapio-digital-active esta ativo), o `window` nao rola mais — o unico
   * scroller e o proprio host (100dvh, overflow-y: auto). Por isso todo
   * scroll-ao-topo deve operar sobre o elemento host.
   *
   * @param smooth quando true usa animacao suave; caso contrario, instantaneo.
   */
  private scrollHostAoTopo(smooth = false): void {
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    hostEl.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'instant' });
  }

  // --- Navegacao por estados ---
  irParaCardapio(): void {
    this.estadoAtual = 'CARDAPIO';
    // Ao voltar do carrinho para a listagem, remover `vista=carrito` da URL
    // (mantendo `categoria` se houver). Se o param ja estiver ausente, e no-op.
    this.removerUrlVista();
  }

  /**
   * Entra na tela do carrinho (estado CARRINHO) e reflete `vista=carrito` na URL,
   * removendo o param `item` (nao estamos mais num detalhe de produto). Mantem o
   * `categoria` via merge. Recarregar/compartilhar a URL com `vista=carrito`
   * reabre o carrinho automaticamente (ver `sincronizarVistaComUrl`).
   *
   * @param opcoes.atualizarUrl quando true (padrao), grava `vista=carrito` na URL.
   *   Quando false (ex.: abertura disparada pela propria URL no load/voltar do
   *   navegador), NAO reescreve a URL, evitando ciclo com a leitura do param.
   */
  irParaCarrinho(opcoes: { atualizarUrl: boolean } = { atualizarUrl: true }): void {
    this.estadoAtual = 'CARRINHO';

    if (opcoes.atualizarUrl) {
      this.atualizarUrlVistaCarrinho();
    }

    // Adia o scroll ate o carrinho renderizar (senao rola o conteudo do menu,
    // que sera substituido, e o carrinho abre na posicao antiga).
    setTimeout(() => this.scrollHostAoTopo());
  }

  irParaCheckout(): void {
    this.checkoutVisivel = true;
  }

  /** Chamado pelo carrinho quando o usuario seleciona um tipo de servico e preenche identificacao */
  onSelecionarTipoServico(dados: CardapioIdentificacaoDTO): void {
    this.tipoPedidoSelecionado = dados.tipo;
    this.nomeClienteCheckout = dados.nome;
    this.telefoneCheckout = dados.telefone;
    this.rucCheckout = dados.ruc || '';
    this.ciCheckout = dados.ci || '';

    // Capturar PaisDDI para salvar no cache de perfil
    this.paisDDICheckout = dados.paisDDI;

    // Formatar endereco completo (apenas delivery)
    if (dados.rua) {
      let enderecoFormatado = dados.rua;
      if (dados.numero) enderecoFormatado += `, N ${dados.numero}`;
      if (dados.complemento) enderecoFormatado += ` - ${dados.complemento}`;
      if (dados.referencia) enderecoFormatado += ` - ${dados.referencia}`;
      // Incluir link Google Maps quando coordenadas disponiveis
      if (dados.latitude && dados.longitude) {
        enderecoFormatado += `\nUbicacion: https://www.google.com/maps?q=${dados.latitude},${dados.longitude}`;
      }
      this.enderecoCheckout = enderecoFormatado;

      // Guardar endereco desagrupado para cache de perfil (incluindo coordenadas)
      this.enderecoIdentificacao = {
        rua: dados.rua,
        numero: dados.numero || '',
        complemento: dados.complemento,
        referencia: dados.referencia,
        latitude: dados.latitude,
        longitude: dados.longitude,
      };
    } else {
      this.enderecoCheckout = '';
      this.enderecoIdentificacao = undefined;
    }

    // Mesa e comanda (apenas MESA/no local, e so o que a loja exige)
    this.numeroMesaCheckout = dados.numeroMesa || '';
    this.numeroComandaCheckout = dados.numeroComanda || '';

    // Frete por distância para o preview (delivery com coordenadas).
    this.atualizarFreteDistancia(dados.tipo);

    this.irParaCheckout();
  }

  /**
   * Busca no backend o frete por DISTÂNCIA (loja → coordenadas de entrega) para
   * o preview bater com o valor cobrado na criação do pedido. Só se aplica a
   * DELIVERY com coordenadas; fora de cobertura/erro cai no valor padrão do
   * `freteConfig` (comportamento anterior).
   */
  private atualizarFreteDistancia(tipo: TipoPedido): void {
    const lat = this.enderecoIdentificacao?.latitude;
    const lng = this.enderecoIdentificacao?.longitude;
    if (tipo !== TipoPedido.DELIVERY || !this.slug || lat == null || lng == null) {
      this.freteDistancia = null;
      return;
    }
    this.cardapioPublicoService.calcularFreteEntrega(this.slug, lat, lng).subscribe({
      next: (valor) => { this.freteDistancia = Number(valor) || 0; },
      error: (err: unknown) => {
        this.freteDistancia = null;
        if (this.ehLojaIndisponivel(err)) this.marcarLojaIndisponivel();
      },
    });
  }

  /** Chamado pelo carrinho para adicionar produto sugerido (abre detalhe do produto) */
  onAdicionarProdutoSugerido(produto: CardapioProdutoDTO): void {
    this.abrirDetalheProduto(produto, { atualizarUrl: true });
  }

  // --- Sidebar ---
  onAbrirMenu(): void { this.sidebarVisivel = true; }

  // --- Busca (estilo Minishop - inline) ---
  toggleBuscar(): void {
    if (this.modoBusca) { this.fecharBusca(); } else { this.abrirBusca(); }
  }

  abrirBusca(): void {
    // Acionar a busca a partir do detalhe volta ao cardapio (onde fica o input).
    if (this.estadoAtual === 'DETALHES_PRODUTO') {
      this.fecharDetalheProduto({ atualizarUrl: true });
    }
    this.modoBusca = true;
    setTimeout(() => {
      // Foca o input que aparece na barra de categorias (mobile).
      document.getElementById('searchInputNav')?.focus();
      this.atualizarAlturaHeader();
    }, 0);
  }

  fecharBusca(): void {
    this.modoBusca = false;
    this.termoBusca = '';
    this.atualizarCacheFiltrados();
    setTimeout(() => this.atualizarAlturaHeader());
  }

  // --- Filtragem de produtos (com cache para evitar recalculo a cada change detection) ---

  /** Chamado pelo template via ngModelChange do campo de busca */
  onTermoBuscaChange(termo: string): void {
    this.termoBusca = termo;
    // Buscar a partir do detalhe volta ao cardapio para exibir os resultados.
    if (termo.trim().length > 0 && this.estadoAtual === 'DETALHES_PRODUTO') {
      this.fecharDetalheProduto({ atualizarUrl: true });
    }
    this.atualizarCacheFiltrados();
  }

  /** Retorna produtos filtrados do cache para uma categoria */
  getProdutosFiltrados(catId: number): CardapioProdutoDTO[] {
    return this.produtosFiltradosCache.get(catId) || [];
  }

  /** Reconstroi o cache de produtos filtrados */
  private atualizarCacheFiltrados(): void {
    const q = this.termoBusca.toLowerCase().trim();
    let totalFiltrado = 0;

    this.categorias.forEach(cat => {
      if (!q) {
        this.produtosFiltradosCache.set(cat.id, cat.produtos);
        totalFiltrado += cat.produtos.length;
      } else {
        const filtrados = cat.produtos.filter(
          (p) => p.nome.toLowerCase().includes(q) || (p.descricao || '').toLowerCase().includes(q)
        );
        this.produtosFiltradosCache.set(cat.id, filtrados);
        totalFiltrado += filtrados.length;
      }
    });

    this.allFilteredCountCache = q ? totalFiltrado : 0;
  }

  // --- TrackBy functions (performance: evita recriacao de DOM no ngFor) ---
  trackByCategoria(index: number, cat: CardapioCategoriaDTO): number {
    return cat.id;
  }

  trackByProduto(index: number, prod: CardapioProdutoDTO): number {
    return prod.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // --- Adicionar produto (navega para detalhe do produto) ---
  onAgregar(produto: CardapioProdutoDTO): void {
    this.abrirDetalheProduto(produto, { atualizarUrl: true });
  }

  /**
   * Adicao rapida a partir do card: soma 1 unidade ao carrinho sem abrir o
   * detalhe. O card so expoe o "+" para produto sem escolha obrigatoria, entao
   * o item vai sempre sem opcoes.
   *
   * `opcoesSelecionadas: []` reproduz o que `converterOpcoes` devolve quando o
   * detalhe e confirmado sem opcoes: assim o fingerprint do CardapioPedidoService
   * bate e a quantidade SOMA na linha existente, em vez de duplicar a linha.
   */
  onAdicionarRapido(produto: CardapioProdutoDTO): void {
    const precoUnitario = produto.precoPromocional ?? produto.preco;

    const item: CardapioItemPedidoDTO = {
      produto,
      quantidade: 1,
      valorTotal: precoUnitario,
      opcoesSelecionadas: [],
    };

    this.pedidoService.addItem(item);
  }

  /**
   * Abre a tela de detalhe do produto (estado DETALHES_PRODUTO) e, opcionalmente,
   * reflete o produto no query param `item` da URL pelo SLUG do nome.
   *
   * @param produto Produto a exibir no detalhe.
   * @param opcoes.atualizarUrl quando true, grava `item={slug-do-nome}` na URL
   *   (mantendo `categoria` via merge). Quando false (ex.: abertura a partir da
   *   propria URL no load), NAO reescreve a URL, evitando ciclo com a leitura do
   *   query param.
   */
  private abrirDetalheProduto(
    produto: CardapioProdutoDTO,
    opcoes: { atualizarUrl: boolean }
  ): void {
    if (this.estadoAtual === 'CARDAPIO') {
      this.scrollDaListagemAntesDoDetalhe = (this.elementRef.nativeElement as HTMLElement).scrollTop;
    }
    this.produtoSelecionado = produto;
    this.estadoAtual = 'DETALHES_PRODUTO';

    if (opcoes.atualizarUrl) {
      this.atualizarUrlItem(produto);
    }

    // Scroll apos o Angular renderizar o novo estado
    setTimeout(() => this.scrollHostAoTopo(), 0);
  }

  /**
   * Fecha a tela de detalhe do produto, voltando ao cardapio. Opcionalmente
   * remove o query param `item` da URL (mantendo `categoria`).
   *
   * @param opcoes.atualizarUrl quando true, remove `item` da URL. Quando false
   *   (ex.: fechamento disparado pela propria mudanca da URL), nao reescreve.
   */
  private fecharDetalheProduto(opcoes: { atualizarUrl: boolean }): void {
    const produtoFechado = this.produtoSelecionado;
    this.estadoAtual = 'CARDAPIO';
    this.produtoSelecionado = null;

    if (opcoes.atualizarUrl) {
      this.removerUrlItem();
    }

    // Aguarda a listagem voltar ao DOM para restaurar a rolagem.
    const posicao = this.scrollDaListagemAntesDoDetalhe;
    this.scrollDaListagemAntesDoDetalhe = null;
    setTimeout(() => this.restaurarRolagemDaListagem(posicao, produtoFechado), 0);
  }

  /**
   * Devolve a listagem ao ponto em que o cliente estava antes de abrir o
   * produto. Sem posicao salva (detalhe aberto pelo link), leva a secao da
   * categoria do produto.
   */
  private restaurarRolagemDaListagem(
    posicao: number | null,
    produto: CardapioProdutoDTO | null
  ): void {
    if (this.estadoAtual !== 'CARDAPIO') return;

    const hostEl = this.elementRef.nativeElement as HTMLElement;
    if (posicao !== null) {
      hostEl.scrollTo({ top: posicao, behavior: 'instant' });
      return;
    }

    const categoria = produto ? this.categoriaDoProduto(produto) : undefined;
    document.getElementById(`cat-${categoria?.id}`)?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  /**
   * Reflete o produto aberto no query param `item` da URL, gravando o SLUG do
   * NOME do produto (legivel) em vez do id.
   *
   * Usa `replaceUrl: false` para EMPILHAR uma entrada no historico do navegador:
   * assim o botao "voltar" do navegador fecha o detalhe e volta a listagem
   * (comportamento natural). A mudanca de query param e captada pelo subscribe
   * de `queryParamMap`, que sincroniza o estado sem reescrever a URL (sem ciclo).
   *
   * Alem de gravar `item`, RECALCULA o param `categoria` para a categoria REAL do
   * produto aberto (derivada por `categoriaDoProduto`), SOBRESCREVENDO uma
   * eventual categoria ativa anterior que estivesse na URL. Isso corrige o caso
   * em que o usuario estava numa categoria e abria um item de outra: o link
   * compartilhado (via `onDetalheCompartilhar`/`window.location.href`) passa a
   * refletir a categoria correta do item.
   *
   * `queryParamsHandling: 'merge'` preserva quaisquer OUTROS query params; o
   * `categoria` informado explicitamente sobrescreve o valor atual. Se a
   * categoria do produto nao for encontrada (caso raro), o `categoria` e mantido
   * como esta (nao pior que o comportamento anterior).
   *
   * Se o nome gerar slug vazio, a URL nao e alterada (comportamento gracioso).
   */
  private atualizarUrlItem(produto: CardapioProdutoDTO): void {
    const slugItem = slugificar(produto?.nome);
    if (!slugItem) {
      return;
    }

    // Recalcular a categoria REAL do produto (e nao a categoria ativa anterior).
    const categoria = this.categoriaDoProduto(produto);
    const slugCategoria = slugificarCategoria(categoria?.nome);

    const queryParams: { [key: string]: string | null } = {
      [this.QUERY_PARAM_ITEM]: slugItem,
      // Abrir o detalhe nunca coexiste com a vista do carrinho. Se o detalhe foi
      // aberto a partir do carrinho (sugestao "Complemente su pedido"), remover
      // `vista=carrito` para nao deixar o param orfao na URL.
      [this.QUERY_PARAM_VISTA]: null,
    };
    // So sobrescreve `categoria` quando conseguimos derivar a categoria do
    // produto; caso contrario deixamos o param como esta (merge preserva).
    if (slugCategoria) {
      queryParams[this.QUERY_PARAM_CATEGORIA] = slugCategoria;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  /**
   * Remove o query param `item` da URL (set para null com merge), mantendo
   * `categoria`. Usado ao fechar/voltar da tela de detalhe.
   *
   * Usa `replaceUrl: true` para nao empilhar uma entrada extra no historico ao
   * fechar manualmente — a entrada da abertura do detalhe e substituida pela da
   * listagem, deixando o historico limpo.
   */
  private removerUrlItem(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.QUERY_PARAM_ITEM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Reflete a entrada no carrinho no query param `vista` da URL
   * (`vista=carrito`), removendo simultaneamente o param `item` (nao estamos
   * mais num detalhe de produto). `categoria` e demais params sao preservados via
   * merge.
   *
   * Usa `replaceUrl: false` para EMPILHAR uma entrada no historico do navegador:
   * assim o botao "voltar" do navegador fecha o carrinho e volta a listagem
   * (mesmo comportamento ja adotado para o detalhe do produto em `atualizarUrlItem`).
   * A mudanca de query param e captada pelo subscribe de `queryParamMap`, que
   * sincroniza o estado sem reescrever a URL (sem ciclo).
   */
  private atualizarUrlVistaCarrinho(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [this.QUERY_PARAM_VISTA]: this.VISTA_CARRINHO,
        [this.QUERY_PARAM_ITEM]: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  /**
   * Remove o query param `vista` da URL (set para null com merge), mantendo
   * `categoria`. Usado ao sair do carrinho (botao "Volver" -> cardapio) e tambem
   * ao navegar do carrinho para qualquer outro estado (tracking/historico/detalhe
   * de produto), evitando deixar um `vista=carrito` orfao na URL.
   *
   * Usa `replaceUrl: true` para nao empilhar uma entrada extra no historico ao
   * fechar manualmente — a entrada da abertura do carrinho e substituida pela do
   * destino, deixando o historico limpo. Se o param ja estiver ausente, o navigate
   * e essencialmente um no-op (nao quebra nem polui o historico).
   */
  private removerUrlVista(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.QUERY_PARAM_VISTA]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Sincroniza a tela do carrinho com o query param `vista` da URL.
   *
   * - Se `vista === 'carrito'` e o carrinho ainda nao esta aberto: abre o carrinho
   *   (sem reescrever a URL). Cobre carga direta da URL, recarregamento e o
   *   avancar do navegador.
   * - Se `vista` ausente (ou diferente de 'carrito') e o carrinho esta aberto:
   *   volta ao cardapio (sem reescrever a URL). Cobre o botao "voltar" do
   *   navegador.
   *
   * Precedencia: `vista=carrito` tem prioridade sobre `item`. Quando ambos vierem
   * na URL (caso atipico), o carrinho prevalece — por isso este metodo e aplicado
   * ANTES da sincronizacao do detalhe, e o detalhe so e sincronizado quando NAO ha
   * vista de carrinho ativa.
   *
   * Nunca reescreve a URL (evita ciclo com a leitura do proprio param).
   *
   * @param vista Valor do param `vista` (ou null se ausente).
   */
  private sincronizarVistaComUrl(vista: string | null): void {
    const querCarrinho = vista === this.VISTA_CARRINHO;

    if (querCarrinho) {
      // Abrir o carrinho apenas se ainda nao estiver nele (evita re-render/scroll
      // e re-escrita desnecessaria).
      if (this.estadoAtual !== 'CARRINHO') {
        this.irParaCarrinho({ atualizarUrl: false });
      }
      return;
    }

    // Sem `vista=carrito`: se estamos no carrinho, voltar ao cardapio (ex.: botao
    // voltar do navegador). Demais estados (tracking/historico/detalhe) nao sao
    // afetados por este param.
    if (this.estadoAtual === 'CARRINHO') {
      this.estadoAtual = 'CARDAPIO';
    }
  }

  onDetalheConfirmar(itemCarrinho: CardapioItemCarrinhoDTO): void {
    if (!this.produtoSelecionado) return;

    // Converter CardapioItemCarrinhoDTO para CardapioItemPedidoDTO
    const opcoesSelecionadas = this.converterOpcoes(
      itemCarrinho.opcoesSelecionadas,
      this.produtoSelecionado
    );

    // Extrair nome da variante: o itemCarrinho.nome vem como "PRODUTO - Variante"
    // quando ha variante selecionada. Basta comparar com o nome base do produto.
    const nomeBase = this.produtoSelecionado.nome;
    const separador = ` - `;
    let nomeVariante: string | undefined;
    if (itemCarrinho.nome !== nomeBase && itemCarrinho.nome.startsWith(nomeBase + separador)) {
      nomeVariante = itemCarrinho.nome.slice(nomeBase.length + separador.length);
    }

    const item: CardapioItemPedidoDTO = {
      produto: this.produtoSelecionado,
      quantidade: itemCarrinho.quantidade,
      valorTotal: itemCarrinho.precoTotal,
      opcoesSelecionadas,
      comentarios: itemCarrinho.observacao,
      nomeVariante,
    };

    this.pedidoService.addItem(item);
    this.fecharDetalheProduto({ atualizarUrl: true });
  }

  onDetalheVoltar(): void {
    this.fecharDetalheProduto({ atualizarUrl: true });
  }

  /**
   * Compartilha a URL atual da tela de detalhe (que ja reflete
   * `?categoria=...&item=...`, abrindo exatamente este produto). Usa a Web Share
   * API quando disponivel (mobile) e cai para copia na area de transferencia
   * (desktop), seguindo o mesmo padrao do dialogo de loja fechada.
   */
  onDetalheCompartilhar(): void {
    const url = window.location.href;
    const titulo = this.produtoSelecionado?.nome || '';
    const preco = this.produtoSelecionado
      ? formatarPreco(
          this.produtoSelecionado.precoPromocional ?? this.produtoSelecionado.preco,
          this.moedaSimbolo,
          this.moedaLocal,
        )
      : '';
    const texto = preco ? `${titulo} - ${preco}` : titulo;

    this.compartilharUrl(url, titulo, texto);
  }

  /**
   * Compartilha a URL especifica de uma categoria da listagem.
   *
   * Monta a URL ABSOLUTA da categoria a partir da base atual (origin + pathname,
   * preservando /menu/{slug}) acrescida apenas de `?categoria={slug}`, derivado do
   * NOME da categoria via `slugificarCategoria`. NAO inclui `item` nem outros
   * params, garantindo que o link compartilhado abra exatamente nesta categoria.
   * Reaproveita a mesma logica de compartilhamento do detalhe (Web Share API +
   * fallback de copia + toast).
   *
   * @param categoria Categoria cujo titulo recebeu o clique no botao compartilhar.
   */
  compartilharCategoria(categoria: CardapioCategoriaDTO): void {
    const slugCategoria = slugificarCategoria(categoria?.nome);
    if (!slugCategoria) {
      return;
    }

    // Base = URL atual sem query params (mantem /menu/{slug}); acrescenta apenas
    // ?categoria={slug}. O slug ja e seguro, mas encode por seguranca no valor.
    const base = `${window.location.origin}${window.location.pathname}`;
    const url = `${base}?${this.QUERY_PARAM_CATEGORIA}=${encodeURIComponent(slugCategoria)}`;

    this.compartilharUrl(url, categoria.nome);
  }

  /**
   * Compartilha a URL especifica de um produto da listagem, a partir do botao de
   * compartilhar presente em cada card.
   *
   * Monta a URL ABSOLUTA do item a partir da base atual (origin + pathname,
   * preservando /menu/{slug}) acrescida de `?categoria={slug}&item={slug}`. O slug
   * da categoria e derivado do NOME da categoria que CONTEM o produto (localizada
   * por id), e o slug do item e derivado do NOME do produto. Assim o link abre
   * exatamente a tela de detalhe deste produto (o param `item` e resolvido pelo
   * fluxo de `sincronizarDetalheComUrl`).
   *
   * Comportamento gracioso: se a categoria do produto nao for encontrada, ainda
   * compartilha apenas com `?item={slug}`. Se o nome do produto gerar slug vazio,
   * nao compartilha (sem URL valida para o item).
   *
   * Reaproveita a logica generica de compartilhamento (Web Share API + fallback
   * de copia + toast).
   *
   * @param produto Produto cujo card recebeu o clique no botao compartilhar.
   */
  compartilharProduto(produto: CardapioProdutoDTO): void {
    const slugItem = slugificar(produto?.nome);
    if (!slugItem) {
      return;
    }

    // Localizar a categoria REAL que contem o produto (mesma derivacao usada por
    // `atualizarUrlItem`, via `categoriaDoProduto`), garantindo que o link do
    // botao de share do card e a URL da tela de detalhe fiquem coerentes.
    const categoria = this.categoriaDoProduto(produto);
    const slugCategoria = slugificarCategoria(categoria?.nome);

    // Base = URL atual sem query params (mantem /menu/{slug}).
    const base = `${window.location.origin}${window.location.pathname}`;
    const params: string[] = [];
    if (slugCategoria) {
      params.push(`${this.QUERY_PARAM_CATEGORIA}=${encodeURIComponent(slugCategoria)}`);
    }
    params.push(`${this.QUERY_PARAM_ITEM}=${encodeURIComponent(slugItem)}`);
    const url = `${base}?${params.join('&')}`;

    const preco = formatarPreco(
      produto.precoPromocional ?? produto.preco,
      this.moedaSimbolo,
      this.moedaLocal,
    );
    const texto = preco ? `${produto.nome} - ${preco}` : produto.nome;

    this.compartilharUrl(url, produto.nome, texto);
  }

  /**
   * Logica generica de compartilhamento reutilizada pelo detalhe do produto e
   * pelas categorias da listagem. Usa a Web Share API quando disponivel (mobile)
   * e cai para a copia na area de transferencia (desktop), com toast de
   * confirmacao. O cancelamento do usuario (AbortError) e tratado silenciosamente.
   *
   * @param url URL absoluta a compartilhar.
   * @param titulo Titulo do compartilhamento (ex.: nome do produto/categoria).
   * @param texto Texto opcional do compartilhamento (default: o proprio titulo).
   */
  private compartilharUrl(url: string, titulo = '', texto?: string): void {
    if (navigator.share) {
      navigator
        .share({ title: titulo, text: texto ?? titulo, url })
        .catch((err) => {
          // Cancelamento do usuario (AbortError) nao deve exibir erro nem fallback.
          if (err?.name === 'AbortError') {
            return;
          }
          // Outros erros: tenta copiar para a area de transferencia.
          this.copiarUrlParaClipboard(url);
        });
    } else {
      this.copiarUrlParaClipboard(url);
    }
  }

  /**
   * Copia a URL para a area de transferencia e exibe um toast de confirmacao.
   * Prioriza `navigator.clipboard`; se indisponivel, usa fallback com textarea
   * temporaria + execCommand. Trata erros graciosamente, sem quebrar a tela.
   */
  private copiarUrlParaClipboard(url: string): void {
    const notificarSucesso = () => {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('cardapio_digital.link_copiado'),
        life: 2000,
      });
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(notificarSucesso)
        .catch(() => this.copiarUrlFallback(url, notificarSucesso));
      return;
    }

    this.copiarUrlFallback(url, notificarSucesso);
  }

  /** Fallback final de copia via textarea temporaria + execCommand. */
  private copiarUrlFallback(url: string, onSucesso: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onSucesso();
    } catch {
      // Ignora silenciosamente: nao ha mais fallback disponivel.
    }
  }

  onConfirmar(item: CardapioItemPedidoDTO): void {
    this.pedidoService.addItem(item);
  }

  /**
   * Converte a lista de opcoes do formato CardapioItemCarrinhoDTO
   * para o formato CardapioItemPedidoDTO, agrupando por titulo do grupo.
   */
  private converterOpcoes(
    opcoesSelecionadas: CardapioOpcaoSelecionadaDTO[] | undefined,
    produto: CardapioProdutoDTO
  ): CardapioItemPedidoDTO['opcoesSelecionadas'] {
    if (!opcoesSelecionadas || opcoesSelecionadas.length === 0) return [];

    // Mapear opcaoId -> nome do grupo usando os modificadores do produto
    const grupoParaOpcao = new Map<number, string>();
    if (produto.modificadores) {
      produto.modificadores.forEach(grupo => {
        grupo.opcoes.forEach(opcao => {
          grupoParaOpcao.set(opcao.id, grupo.nome);
        });
      });
    }

    // Agrupar opcoes selecionadas por titulo de grupo
    const grupos = new Map<string, { titulo: string; quantidade: number; valorAdicional: number }[]>();
    opcoesSelecionadas.forEach(opcao => {
      const grupoNome = grupoParaOpcao.get(opcao.opcaoId) || '';
      if (!grupos.has(grupoNome)) {
        grupos.set(grupoNome, []);
      }
      grupos.get(grupoNome)!.push({
        titulo: opcao.nome,
        quantidade: opcao.quantidade || 1,
        valorAdicional: opcao.precoAdicional,
      });
    });

    return Array.from(grupos.entries()).map(([grupoTitulo, itens]) => ({
      grupoTitulo,
      itens,
    }));
  }

  /** Retorna a quantidade total de um produto no carrinho (para exibir badge) */
  quantidadeNoCarrinho(produtoId: number): number {
    return this.pedidoService.quantidadePorProduto(produtoId);
  }

  // --- Footer ---
  onVerPedido(): void {
    // Bloquear acesso ao carrinho se a loja estiver fechada (apenas no modo publico com slug)
    if (this.slug && this.estabelecimento && !this.estabelecimento.aberto) {
      this.lojaFechadaVisivel = true;
      return;
    }
    this.irParaCarrinho();
  }

  // --- Checkout ---
  onCheckoutVoltar(): void {
    this.checkoutVisivel = false;
    // Sair do checkout e desistir da cobranca: alem de parar o acompanhamento, o QR e cancelado
    // no backend — um pagamento feito depois disso e recusado e revertido para o cliente.
    this.onCancelarCobrancaQr();
  }

  /**
   * Abre o aviso modal. As chaves vivem em `cardapio_digital.erro`.
   *
   * `mensagemEspecifica` vence o texto generico — usada quando o backend diz o motivo
   * (ex.: "O telefone deve ter entre 8 e 20 caracteres").
   */
  private mostrarAviso(tituloKey: string, mensagemKey: string, botaoKey: string, mensagemEspecifica = ''): void {
    const prefixo = 'cardapio_digital.erro.';
    this.aviso = {
      visivel: true,
      titulo: this.translate.instant(prefixo + tituloKey),
      mensagem: mensagemEspecifica || this.translate.instant(prefixo + mensagemKey),
      textoBotao: this.translate.instant(prefixo + botaoKey),
    };
  }

  /**
   * Motivo legivel de um erro HTTP: `errors[].mensagem` nas falhas de validacao, `message` no
   * restante. Devolve vazio em erro de rede (status 0, sem corpo), para o chamador cair no
   * texto generico.
   */
  private extrairMotivoDoErro(err: unknown): string {
    if (!(err instanceof HttpErrorResponse) || !err.status) return '';
    const corpo = err.error as RespostaDeErroDaApi | null | undefined;
    if (!corpo) return '';

    // O nome tecnico do campo nao vai para a tela, mas fica registrado para diagnostico:
    // mensagens como "Campo ou valor obrigatorio ausente" nao dizem sozinhas o que falhou.
    if (corpo.errors?.length) {
      console.warn('[cardapio] pedido recusado pelo backend:', corpo.errors);
    }

    const validacoes = (corpo.errors ?? [])
      .map(e => {
        const mensagem = e?.mensagem || e?.message;
        if (!mensagem) return '';
        const rotulo = this.rotuloDoCampo(e?.campo);
        return rotulo ? `${rotulo}: ${mensagem}` : mensagem;
      })
      .filter((m): m is string => !!m);
    // Separador visivel: sem ele, varios erros viram uma frase corrida.
    if (validacoes.length) return validacoes.join(' • ');

    return corpo.message || '';
  }

  /**
   * Nome do campo como o cliente o conhece na tela. Volta vazio quando o campo e desconhecido —
   * prefixar a mensagem com algo como `itens[0]` so confundiria quem esta comprando.
   */
  private rotuloDoCampo(campo?: string): string {
    if (!campo) return '';
    const chave = `cardapio_digital.erro.campo.${campo}`;
    const rotulo = this.translate.instant(chave);
    return rotulo === chave ? '' : rotulo;
  }

  /**
   * Gera a cobranca por QR assim que o cliente escolhe QR Code ou cartao no checkout.
   *
   * O pedido ainda NAO existe: o backend guarda o payload junto da cobranca e cria o pedido
   * quando a Bancard confirmar o pagamento. Aqui a tela so exibe o codigo e fica acompanhando.
   */
  async onGerarCobrancaQr(form: CardapioCheckoutFormDTO): Promise<void> {
    // Sem slug e o preview interno do lojista: nao ha loja para cobrar.
    if (!this.slug) return;

    // Mesma forma e cobranca ainda valida: reaproveita o QR que ja esta na tela em vez de
    // gerar outro na Bancard a cada clique.
    if (this.estadoCobrancaQr.cobranca && this.formaDaCobrancaQr === form.formaPagamentoValor) {
      this.formDaCobrancaQr = form;
      return;
    }

    this.pararPollingCobrancaQr();
    this.estadoCobrancaQr = { gerando: true, confirmado: false, cobranca: null, erro: null };
    this.codigoDePedidoDaCobrancaQr = null;

    const { itensPedido, subtotal, frete, total } = this.calcularTotaisDoCheckout(form);
    const dto = this.montarPedidoPublicoDTO(form, itensPedido, subtotal, frete, total);

    try {
      const cobranca = await firstValueFrom(
        this.cardapioPublicoService.criarCobrancaQr(this.slug, dto).pipe(timeout(20000))
      );
      // Cobranca sem o meio de pagamento nao serve para nada: sem QR o cliente nao tem o que ler,
      // sem link nao tem o que abrir. Melhor tratar como falha e oferecer "tentar de novo" do que
      // deixar a tela esperando um pagamento impossivel.
      const meioDePagamentoAusente = form.formaPagamentoValor === 'TARJETA'
        ? !cobranca.linkUrl
        : !cobranca.qrData && !cobranca.urlQr;
      if (meioDePagamentoAusente) {
        this.estadoCobrancaQr = {
          gerando: false,
          confirmado: false,
          cobranca: null,
          erro: this.translate.instant('cardapio_digital.checkout.qr_erro'),
        };
        return;
      }

      this.estadoCobrancaQr = { gerando: false, confirmado: false, cobranca, erro: null };
      this.formaDaCobrancaQr = form.formaPagamentoValor || null;
      this.formDaCobrancaQr = form;
      this.iniciarPollingCobrancaQr(cobranca.intencaoDeRecebimentoId);
    } catch (err) {
      console.error('[CardapioDigital] Erro ao gerar a cobranca por QR:', err);
      if (this.ehLojaIndisponivel(err)) {
        this.estadoCobrancaQr = { gerando: false, confirmado: false, cobranca: null, erro: null };
        this.marcarLojaIndisponivel();
        return;
      }
      const motivo = this.extrairMotivoDoErro(err);
      this.estadoCobrancaQr = {
        gerando: false,
        confirmado: false,
        cobranca: null,
        erro: motivo || this.translate.instant('cardapio_digital.checkout.qr_erro'),
      };
    }
  }

  /**
   * O cliente trocou para uma forma que nao passa pela Bancard: a cobranca em aberto morre.
   *
   * Some da tela E e cancelada no backend: o QR ja exibido continua pagavel na Bancard, e um
   * pagamento que chegasse depois viraria pedido sem ninguem estar esperando por ele. Cobranca ja
   * confirmada nao se cancela — o pedido nasceu dela.
   */
  onCancelarCobrancaQr(): void {
    const intencaoId = this.estadoCobrancaQr?.cobranca?.intencaoDeRecebimentoId;
    const jaConfirmado = this.estadoCobrancaQr?.confirmado === true;

    this.pararPollingCobrancaQr();
    this.estadoCobrancaQr = { gerando: false, confirmado: false, cobranca: null, erro: null };
    this.formaDaCobrancaQr = null;
    this.formDaCobrancaQr = null;
    this.codigoDePedidoDaCobrancaQr = null;

    if (!this.slug || intencaoId == null || jaConfirmado) return;

    // Sem aviso na tela: o cliente ja seguiu para outra forma de pagamento e nao ha nada que ele
    // possa fazer com a falha. Quem recusa o pagamento e o backend, pelo status da cobranca.
    this.cardapioPublicoService.cancelarCobrancaQr(this.slug, intencaoId).subscribe({
      error: () => {},
    });
  }

  /**
   * Acompanha o pagamento a cada 3 segundos.
   *
   * Quem confirma e o callback da Bancard, nunca a tela — por isso a unica saida daqui e o
   * backend dizer que a cobranca foi paga. Ao confirmar, o pedido ja nasceu e a tela segue
   * sozinha para o acompanhamento.
   */
  private iniciarPollingCobrancaQr(intencaoId: number): void {
    if (!this.slug) return;
    this.pollingCobrancaQr = timer(3000, 3000)
      .pipe(
        switchMap(() =>
          this.cardapioPublicoService.consultarCobrancaQr(this.slug!, intencaoId).pipe(
            catchError(() => of(null))
          )
        )
      )
      .subscribe(status => {
        if (!status?.confirmado) return;

        this.pararPollingCobrancaQr();
        this.estadoCobrancaQr = { ...this.estadoCobrancaQr, confirmado: true };
        this.codigoDePedidoDaCobrancaQr = status.codigoDePedido ?? null;

        if (this.formDaCobrancaQr) {
          this.onPedidoFinalizado(this.formDaCobrancaQr);
        }
      });
  }

  private pararPollingCobrancaQr(): void {
    this.pollingCobrancaQr?.unsubscribe();
    this.pollingCobrancaQr = undefined;
  }

  /**
   * Totais do checkout. O frete por distância (backend) tem prioridade no delivery — e o mesmo
   * valor que sera recalculado e cobrado la.
   */
  private calcularTotaisDoCheckout(form: CardapioCheckoutFormDTO): {
    itensPedido: CardapioItemPedidoDTO[];
    subtotal: number;
    frete: number;
    total: number;
  } {
    const itensPedido = this.pedidoService.itens;
    const subtotal = itensPedido.reduce((acc, item) => acc + item.valorTotal, 0);
    const frete = (form.tipoPedido === TipoPedido.DELIVERY && this.freteDistancia != null)
      ? this.freteDistancia
      : this.freteService.calcularFrete(subtotal, form.tipoPedido, this.freteConfig);
    return { itensPedido, subtotal, frete, total: subtotal + frete };
  }

  async onPedidoFinalizado(form: CardapioCheckoutFormDTO): Promise<void> {
    const { itensPedido, subtotal, frete, total } = this.calcularTotaisDoCheckout(form);

    // Converter CardapioItemPedidoDTO para CardapioItemCarrinhoDTO (formato esperado pelo WhatsApp)
    const itensCarrinhoConvertidos: CardapioItemCarrinhoDTO[] = itensPedido.map(item => ({
      produtoId: item.produto.id,
      nome: item.produto.nome,
      quantidade: item.quantidade,
      precoUnitario: item.valorTotal / item.quantidade,
      precoTotal: item.valorTotal,
      imagem: item.produto.foto || null,
      observacao: item.comentarios,
      opcoesSelecionadas: (item.opcoesSelecionadas || []).flatMap(grupo =>
        grupo.itens.map(opcao => ({
          opcaoId: 0,
          nome: opcao.titulo,
          precoAdicional: opcao.valorAdicional,
        }))
      ),
      uid: `${item.produto.id}-${JSON.stringify(item.opcoesSelecionadas)}`,
    }));

    // Tentar enviar pedido para o backend (apenas modo publico com slug)
    let codigoDePedido: number | null = null;
    // Sem isto o fluxo seguia para a tela de sucesso mesmo com o POST falhando, e o cliente lia
    // "Recebemos seu pedido!" sem que houvesse pedido.
    let envioFalhou = false;
    // O 422 (loja fechada) ja exibe mensagem propria — nao empilhar uma segunda.
    let erroJaComunicado = false;
    // Sem isto, um 400 de validacao virava "confira sua conexao" na tela.
    let motivoDoBackend = '';

    // Pagamento por QR ja confirmado: o pedido foi criado pelo callback da Bancard, com o payload
    // guardado na cobranca. Repetir o POST aqui criaria um segundo pedido para o mesmo pagamento.
    if (this.slug && this.estadoCobrancaQr.confirmado) {
      codigoDePedido = this.codigoDePedidoDaCobrancaQr;
      // O QR ja foi pago; exibi-lo de novo no acompanhamento so induziria a pagar duas vezes.
      this.cobrancaQr = null;
      this.onCancelarCobrancaQr();
    } else if (this.slug) {
      const pedidoPublicoDTO = this.montarPedidoPublicoDTO(form, itensPedido, subtotal, frete, total);
      try {
        const response = await firstValueFrom(
          this.cardapioPublicoService.criarPedido(this.slug, pedidoPublicoDTO).pipe(
            timeout(5000),
            catchError((err) => {
              console.error('[CardapioDigital] Erro ao enviar pedido:', err);
              motivoDoBackend = this.extrairMotivoDoErro(err);
              // 403: MiniShop desabilitado — a loja nao aceita mais pedido, mostra a tela de indisponivel
              if (this.ehLojaIndisponivel(err)) {
                const indisponivel = this.translate.instant('cardapio_digital.loja_indisponivel');
                this.messageService.add({
                  severity: 'error',
                  summary: indisponivel,
                  life: 5000,
                });
                this.marcarLojaIndisponivel();
                erroJaComunicado = true;
              } else if (err instanceof HttpErrorResponse && err.status === 422 && this.ehLojaFechada(err, motivoDoBackend)) {
                // 422 de estabelecimento fechado / fora do horario de atendimento
                this.messageService.add({
                  severity: 'warn',
                  summary: this.translate.instant('cardapio_digital.loja_fechada.titulo'),
                  detail: this.translate.instant('cardapio_digital.loja_fechada.fora_do_horario'),
                  life: 5000,
                });
                // Atualizar status de aberto localmente e exibir dialog
                if (this.estabelecimento) {
                  this.estabelecimento.aberto = false;
                }
                this.lojaFechadaVisivel = true;
                erroJaComunicado = true;
              } else if (err instanceof HttpErrorResponse && err.status === 422 && motivoDoBackend) {
                // Outros 422 (regra de negocio do pedido): mostra o motivo sem marcar a loja como fechada
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('erro'),
                  detail: motivoDoBackend,
                  life: 6000,
                });
                erroJaComunicado = true;
              }
              return of(null);
            })
          )
        );
        if (response) {
          codigoDePedido = response.codigoDePedido;
          this.cobrancaQr = response.intencaoDeRecebimentoId
            ? {
                intencaoDeRecebimentoId: response.intencaoDeRecebimentoId,
                qrData: response.qrData,
                urlQr: response.urlQr,
              }
            : null;
        } else {
          envioFalhou = true;
        }
      } catch (e) {
        console.error('[CardapioDigital] Erro inesperado:', e);
        envioFalhou = true;
      }

      // Aborta ANTES de limpar o carrinho e de ir para o tracking: sem pedido no backend nao ha
      // nada para acompanhar. O carrinho e o formulario ficam intactos para o cliente tentar de
      // novo — a decisao de tentar sozinho (retry automatico) ficou fora deste ajuste.
      if (envioFalhou) {
        if (!erroJaComunicado) {
          this.mostrarAviso('envio_pedido_titulo', 'envio_pedido_detalhe', 'envio_pedido_acao', motivoDoBackend);
        }
        return;
      }
    }

    const pedido: CardapioPedidoDTO = {
      idCurto: this.whatsappService.gerarIdCurto(),
      itens: itensCarrinhoConvertidos,
      tipoPedido: form.tipoPedido,
      nomeCliente: form.nomeCliente,
      telefone: form.telefone || undefined,
      endereco: form.endereco || undefined,
      numeroMesa: form.numeroMesa || undefined,
      comentario: form.comentario || undefined,
      formaPagamento: form.formaPagamento || undefined,
      subtotal,
      frete,
      total,
      valorPago: form.valorPago || undefined,
      troco: form.troco !== undefined ? form.troco : undefined,
    };

    // Gerar o link do WhatsApp sem abrir automaticamente.
    // O usuario devera clicar no botao "Enviar" na tela de tracking.
    const mensagemWhatsapp = this.whatsappService.gerarMensagem(
      pedido,
      this.moedaSimbolo,
      this.moedaLocal
    );
    const linkWhatsapp = this.whatsappService.gerarLinkWhatsapp(
      this.estabelecimento.telefoneWhatsapp,
      mensagemWhatsapp
    );

    // Salvar dados do pedido para exibir na tela de tracking
    this.trackingAtual = {
      idCurto: pedido.idCurto,
      nomeCliente: pedido.nomeCliente,
      tipoPedido: pedido.tipoPedido,
      formaPagamento: pedido.formaPagamento || '',
      valorPago: pedido.valorPago,
      troco: pedido.troco,
      subtotal: pedido.subtotal,
      frete: pedido.frete,
      total: pedido.total,
      endereco: pedido.endereco,
      numeroMesa: pedido.numeroMesa,
      linkWhatsapp,
      itens: itensPedido.map(item => ({
        nome: item.produto.nome,
        foto: item.produto.foto,
        nomeVariante: item.nomeVariante,
        precoUnitario: item.quantidade > 0 ? item.valorTotal / item.quantidade : 0,
        quantidade: item.quantidade,
        precoTotal: item.valorTotal,
        opcoes: item.opcoesSelecionadas
          .flatMap(g => g.itens.map(o => o.titulo))
          .filter(Boolean),
        opcoesSelecionadas: item.opcoesSelecionadas,
        comentarios: item.comentarios,
        // Preservar o ID real do produto para permitir copia correta do pedido
        produtoCardapioId: item.produto.id > 0 ? item.produto.id : null,
      })),
      codigoDePedido,
      estadoDePedido: 'ABERTO',
    };

    // Salvar no historico antes de limpar o carrinho
    const slugHistorico = this.slug || 'preview';
    const pedidoHistorico: PedidoHistoricoDTO = {
      idCurto: pedido.idCurto,
      data: new Date().toISOString(),
      nomeCliente: form.nomeCliente,
      tipoPedido: form.tipoPedido,
      itens: itensPedido.map(item => ({
        nome: item.nomeVariante ? `${item.produto.nome} - ${item.nomeVariante}` : item.produto.nome,
        quantidade: item.quantidade,
        precoTotal: item.valorTotal,
        foto: item.produto.foto,
        nomeVariante: item.nomeVariante,
        opcoesSelecionadas: item.opcoesSelecionadas,
        comentarios: item.comentarios,
        // Preservar o ID real do produto para permitir copia correta do pedido
        produtoCardapioId: item.produto.id > 0 ? item.produto.id : null,
      })),
      subtotal,
      frete,
      total,
      formaPagamento: form.formaPagamento || undefined,
      endereco: form.endereco || undefined,
      numeroMesa: form.numeroMesa || undefined,
      status: 'ENVIADO',
      empresaSlug: slugHistorico,
      codigoDePedido,
      estadoDePedido: 'ABERTO',
    };
    this.historicoService.adicionarPedido(slugHistorico, pedidoHistorico);
    // pedidosHistorico e temHistorico sao atualizados automaticamente via historicoObservable

    // Persistir o idCurto do pedido em tracking para restaurar apos refresh
    this.historicoService.salvarTrackingPendente(slugHistorico, pedido.idCurto);

    // Salvar perfil do cliente no cache (preenchimento automatico no proximo pedido)
    this.salvarPerfilCliente(slugHistorico, form);

    this.pedidoService.clear();
    this.checkoutVisivel = false;
    this.veioDoHistorico = false;
    this.estadoAtual = 'TRACKING';
    // Saimos do carrinho rumo ao tracking: limpar `vista=carrito` da URL para
    // nao deixar o param orfao.
    this.removerUrlVista();
  }

  /** Monta e salva o perfil do cliente no cache local */
  private salvarPerfilCliente(slug: string, form: CardapioCheckoutFormDTO): void {
    if (!this.paisDDICheckout) return;

    // Extrair telefone sem o prefixo DDI
    const ddiPrefix = this.paisDDICheckout.ddi;
    let telefoneSemDDI = form.telefone;
    if (telefoneSemDDI.startsWith(ddiPrefix)) {
      telefoneSemDDI = telefoneSemDDI.slice(ddiPrefix.length);
    }

    const perfil: CardapioClientePerfilDTO = {
      nome: form.nomeCliente,
      paisDDI: this.paisDDICheckout,
      telefone: telefoneSemDDI,
      ruc: form.ruc || undefined,
      ci: form.ci || undefined,
    };

    // Adicionar endereco se for delivery e dados estiverem disponiveis
    if (form.tipoPedido === TipoPedido.DELIVERY && this.enderecoIdentificacao) {
      perfil.endereco = { ...this.enderecoIdentificacao };
    }

    this.clienteCacheService.salvarPerfil(slug, perfil);
  }

  irParaCardapioAposTracking(): void {
    // Limpar tracking pendente ao sair voluntariamente (usuario decidiu nao acompanhar mais)
    const slug = this.slug || 'preview';
    this.historicoService.limparTrackingPendente(slug);
    this.trackingAtual = null;
    this.cobrancaQr = null;
    this.veioDoHistorico = false;
    this.estadoAtual = 'CARDAPIO';
    this.scrollHostAoTopo();
  }

  /** Retorna ao historico ao sair do tracking aberto a partir do historico */
  voltarParaHistorico(): void {
    // Limpar tracking pendente ao voltar ao historico manualmente
    const slug = this.slug || 'preview';
    this.historicoService.limparTrackingPendente(slug);
    this.trackingAtual = null;
    this.cobrancaQr = null;
    this.veioDoHistorico = false;
    this.estadoAtual = 'HISTORICO';
    this.scrollHostAoTopo();
  }

  /**
   * Restaura o tracking ativo apos refresh da pagina.
   * Verifica se ha um idCurto de tracking pendente no localStorage.
   * Se o pedido correspondente existir no historico e tiver codigoDePedido, reconstroi
   * o trackingAtual e navega para o estado TRACKING automaticamente.
   */
  private restaurarTrackingAposRefresh(slug: string): void {
    const pedidoPendente = this.historicoService.recuperarTrackingPendente(slug);
    if (!pedidoPendente) return;

    // Reconstruir trackingAtual a partir do historico (sem linkWhatsapp pois ja foi enviado)
    this.trackingAtual = {
      idCurto: pedidoPendente.idCurto,
      nomeCliente: pedidoPendente.nomeCliente,
      tipoPedido: pedidoPendente.tipoPedido,
      formaPagamento: pedidoPendente.formaPagamento || '',
      subtotal: pedidoPendente.subtotal,
      frete: pedidoPendente.frete,
      total: pedidoPendente.total,
      endereco: pedidoPendente.endereco,
      numeroMesa: pedidoPendente.numeroMesa,
      codigoDePedido: pedidoPendente.codigoDePedido || null,
      // Usar estado salvo no historico (atualizado pelo polling) ou ABERTO como fallback
      estadoDePedido: pedidoPendente.estadoDePedido || 'ABERTO',
      itens: pedidoPendente.itens.map(item => ({
        nome: item.nome,
        quantidade: item.quantidade,
        precoUnitario: item.quantidade > 0 ? item.precoTotal / item.quantidade : 0,
        precoTotal: item.precoTotal,
        foto: item.foto,
        nomeVariante: item.nomeVariante,
        opcoesSelecionadas: item.opcoesSelecionadas,
        comentarios: item.comentarios,
        // ID real do produto — necessario para recriar o pedido ao copiar
        produtoCardapioId: item.produtoCardapioId ?? null,
      })),
    };

    // Navegar para o estado TRACKING sem exibir o modal do WhatsApp (veioDoHistorico=true)
    this.veioDoHistorico = false;
    this.estadoAtual = 'TRACKING';
  }

  /** Copia os itens do tracking para o carrinho e navega para o estado CARRINHO */
  onCopiarPedido(tracking: CardapioTrackingDTO): void {
    if (!tracking.itens?.length) return;

    // Limpar carrinho atual
    this.pedidoService.clear();

    // Converter e adicionar cada item
    const itensConvertidos = this.converterTrackingParaCarrinho(tracking.itens);
    itensConvertidos.forEach(item => this.pedidoService.addItem(item));

    // Navegar para o carrinho (reflete `vista=carrito` na URL via irParaCarrinho).
    this.irParaCarrinho();
  }

  /** Converte itens do tracking para o formato do carrinho (CardapioItemPedidoDTO) */
  private converterTrackingParaCarrinho(itens: CardapioTrackingItemDTO[]): CardapioItemPedidoDTO[] {
    return itens.map((item, index) => ({
      produto: {
        // Usar o ID real do produto quando disponivel (campo salvo desde a correcao do bug de copia).
        // Pedidos antigos sem o campo usam ID negativo como fallback (nao sera enviado ao backend,
        // mas permite exibir os itens no carrinho para revisao do usuario).
        id: item.produtoCardapioId && item.produtoCardapioId > 0 ? item.produtoCardapioId : -(index + 1),
        nome: item.nome,
        preco: item.precoUnitario,
        descricao: null,
        // Usar foto do tracking se disponivel (campo enriquecido)
        foto: item.foto ?? null,
        modificadores: [],
      } as CardapioProdutoDTO,
      quantidade: item.quantidade,
      valorTotal: item.precoTotal,
      // Usar opcoesSelecionadas estruturadas se disponivel (campo enriquecido)
      opcoesSelecionadas: item.opcoesSelecionadas ?? [],
      nomeVariante: item.nomeVariante,
      comentarios: item.comentarios,
    }));
  }

  /** Navega para o estado HISTORICO */
  irParaHistorico(): void {
    this.sidebarVisivel = false;
    this.estadoAtual = 'HISTORICO';
    // A sidebar pode ser aberta a partir do carrinho; limpar `vista=carrito` para
    // nao deixar o param orfao na URL ao ir para o historico.
    this.removerUrlVista();
    this.scrollHostAoTopo();
  }

  /** Abre o tracking de um pedido do historico (sem link WhatsApp, pois ja foi enviado) */
  onVerTrackingDoHistorico(pedido: PedidoHistoricoDTO): void {
    this.trackingAtual = {
      idCurto: pedido.idCurto,
      nomeCliente: pedido.nomeCliente,
      tipoPedido: pedido.tipoPedido,
      formaPagamento: pedido.formaPagamento || '',
      subtotal: pedido.subtotal,
      frete: pedido.frete,
      total: pedido.total,
      endereco: pedido.endereco,
      numeroMesa: pedido.numeroMesa,
      codigoDePedido: pedido.codigoDePedido || null,
      // linkWhatsapp nao disponivel para pedidos do historico (ja foram enviados)
      itens: pedido.itens.map(item => ({
        nome: item.nome,
        quantidade: item.quantidade,
        precoUnitario: item.quantidade > 0 ? item.precoTotal / item.quantidade : 0,
        precoTotal: item.precoTotal,
        // Repassar campos enriquecidos (serao undefined em pedidos antigos - template ja tem fallback)
        foto: item.foto,
        nomeVariante: item.nomeVariante,
        opcoesSelecionadas: item.opcoesSelecionadas,
        comentarios: item.comentarios,
        // ID real do produto — necessario para recriar o pedido ao copiar
        produtoCardapioId: item.produtoCardapioId ?? null,
      })),
    };
    this.veioDoHistorico = true;
    this.estadoAtual = 'TRACKING';
    this.scrollHostAoTopo();
  }

  /** Limpa historico (chamado pelo componente filho apos confirmacao) */
  onLimparHistorico(): void {
    const slug = this.slug || 'preview';
    this.historicoService.limparHistorico(slug);
    // pedidosHistorico e temHistorico sao atualizados automaticamente via historicoObservable
    this.irParaCardapio();
  }

  // --- Formatacao de preco (delegado para utilitario) ---
  formatarPreco(valor: number): string {
    return formatarPreco(valor, this.moedaSimbolo, this.moedaLocal);
  }

  // --- Metodos privados ---

  /**
   * Converte os dados do carrinho e formulario de checkout
   * para o formato esperado pelo backend (CardapioPedidoPublicoDTO).
   */
  private montarPedidoPublicoDTO(
    form: CardapioCheckoutFormDTO,
    itensPedido: CardapioItemPedidoDTO[],
    subtotal: number,
    frete: number,
    total: number
  ): CardapioPedidoPublicoDTO {
    return {
      nomeCliente: form.nomeCliente,
      telefoneCliente: form.telefone,
      tipoPedido: form.tipoPedido,
      enderecoEntrega: form.endereco || null,
      referenciaEntrega: this.enderecoIdentificacao?.referencia || null,
      latitudeEntrega: this.enderecoIdentificacao?.latitude || null,
      longitudeEntrega: this.enderecoIdentificacao?.longitude || null,
      numeroMesa: form.numeroMesa || null,
      numeroComanda: form.numeroComanda || null,
      comentario: form.comentario || null,
      // Valor do enum, nunca o rotulo: o backend resolve `FormaDePagamentoPedido` desta string,
      // e "QR Code"/"Codigo QR" nao casam com forma nenhuma — o pedido ficava sem forma e sem
      // cobranca gerada, em silencio.
      formaPagamento: form.formaPagamentoValor || null,
      ruc: form.ruc || null,
      ci: form.ci || null,
      itens: itensPedido.map(item => this.montarItemPublicoDTO(item)),
      subtotal,
      frete,
      total,
    };
  }

  /**
   * Converte um CardapioItemPedidoDTO (carrinho) para CardapioPedidoPublicoItemDTO (backend).
   */
  private montarItemPublicoDTO(item: CardapioItemPedidoDTO): CardapioPedidoPublicoItemDTO {
    const opcoesModificadores = this.extrairModificadoresPedido(item);
    const varianteId = this.resolverVarianteId(item);

    return {
      produtoCardapioId: item.produto.id,
      quantidade: item.quantidade,
      valorUnitario: item.quantidade > 0 ? item.valorTotal / item.quantidade : 0,
      valorTotal: item.valorTotal,
      observacao: item.comentarios || null,
      opcoesModificadores: opcoesModificadores.length > 0 ? opcoesModificadores : null,
      varianteId,
    };
  }

  /**
   * Extrai os modificadores selecionados com quantidade a partir do produto original.
   * Faz match por nome da opcao dentro de cada grupo de modificador.
   * Retorna objetos { opcaoModificadorId, quantidade } para o POST /cardapio/{slug}/pedido.
   */
  private extrairModificadoresPedido(item: CardapioItemPedidoDTO): CardapioOpcaoModificadorPedidoDTO[] {
    if (!item.opcoesSelecionadas || item.opcoesSelecionadas.length === 0) return [];
    if (!item.produto.modificadores || item.produto.modificadores.length === 0) return [];

    const resultado: CardapioOpcaoModificadorPedidoDTO[] = [];

    item.opcoesSelecionadas.forEach(grupo => {
      grupo.itens.forEach(opcaoSelecionada => {
        const grupoOriginal = item.produto.modificadores.find(
          m => m.nome === grupo.grupoTitulo
        );
        if (grupoOriginal) {
          const opcaoOriginal = grupoOriginal.opcoes.find(
            o => o.nome === opcaoSelecionada.titulo
          );
          if (opcaoOriginal) {
            resultado.push({
              opcaoModificadorId: opcaoOriginal.id,
              quantidade: opcaoSelecionada.quantidade || 1,
            });
          }
        }
      });
    });

    return resultado;
  }

  /**
   * Resolve o varianteId a partir do nomeVariante do item.
   * Busca no array produto.variantes pelo nome.
   */
  private resolverVarianteId(item: CardapioItemPedidoDTO): number | null {
    if (!item.nomeVariante) return null;
    if (!item.produto.variantes || item.produto.variantes.length === 0) return null;

    const variante = item.produto.variantes.find(
      v => v.nome === item.nomeVariante
    );
    return variante?.id ?? null;
  }

  private carregarBannerMiniShop(idEmpresa: string): void {
    this.empresaConfigService.get('/configuracaoEmpresa').subscribe({
      next: (response: any) => {
        const config = response.content?.[0];
        if (!config) return;

        // Usar config.id quando disponível para garantir que o ID da empresa está correto,
        // pois é o campo definitivo retornado pelo endpoint /configuracaoEmpresa.
        const empresaIdFinal = config.id?.toString() || idEmpresa;

        if (config.minishopImagemDeTitulo) {
          const bannerUrl = `${environment.imgHost}/${empresaIdFinal}/configuracao_empresa/${config.minishopImagemDeTitulo}`;
          this.estabelecimento.banner = bannerUrl;
          // Sincronizar imagemDeTitulo para garantir consistência com o template
          if (!this.estabelecimento.imagemDeTitulo) {
            this.estabelecimento.imagemDeTitulo = bannerUrl;
          }
        }
      },
      error: () => {},
    });
  }

  private loadTranslations(): void {
    this.texts = this.translate.instant('cardapio_digital');
  }

  /** Link do WhatsApp do estabelecimento (exibido como botao no header). */
  getWhatsappLink(): string {
    return `https://wa.me/${this.estabelecimento?.telefoneWhatsapp}`;
  }

  /** Link do Instagram do estabelecimento (exibido como botao no header). */
  getInstagramLink(): string {
    return `https://instagram.com/${this.estabelecimento?.perfilInstagram}`;
  }

  /**
   * Aplica as cores de configuracao do estabelecimento (Mini Shop) como CSS custom properties.
   */
  private aplicarTemaEstabelecimento(): void {
    if (!this.estabelecimento) return;

    const host = this.elementRef.nativeElement as HTMLElement;

    const corDeFundo = this.estabelecimento.corDeFundo?.trim() || '#ffffff';
    const corDaFonte = this.estabelecimento.corDaFonte?.trim() || '#0f172a';
    const botoesCorDeFundo = this.estabelecimento.botoesCorDeFundo?.trim() || '#007BFF';
    const botoesCorDaFonte = this.estabelecimento.botoesCorDaFonte?.trim() || '#ffffff';
    const gruposCorDeFundo = this.estabelecimento.gruposCorDeFundo?.trim() || botoesCorDeFundo;
    const gruposCorDaFonte = this.estabelecimento.gruposCorDaFonte?.trim() || botoesCorDaFonte;
    // Card herda as cores de grupos; cabecalho herda as cores de botoes.
    const cardCorDeFundo = this.estabelecimento.cardCorDeFundo?.trim() || gruposCorDeFundo;
    const cardCorDaFonte = this.estabelecimento.cardCorDaFonte?.trim() || gruposCorDaFonte;
    const cabecalhoCorDeFundo = this.estabelecimento.cabecalhoCorDeFundo?.trim() || botoesCorDeFundo;
    const cabecalhoCorDaFonte = this.estabelecimento.cabecalhoCorDaFonte?.trim() || botoesCorDaFonte;

    // Quando o fundo e muito claro (ex: branco puro), clarear nao funciona.
    // Nesse caso, escurecemos levemente o surface e aumentamos o contraste das bordas.
    const fundoClaro = corEhClara(corDeFundo);

    const variaveis: Record<string, string> = {
      '--cardapio-bg': corDeFundo,
      '--cardapio-surface': fundoClaro ? ajustarCor(corDeFundo, -6) : ajustarCor(corDeFundo, 10),
      '--cardapio-surface-hover': fundoClaro ? ajustarCor(corDeFundo, -14) : ajustarCor(corDeFundo, 20),
      '--cardapio-border': fundoClaro ? ajustarCor(corDeFundo, -42) : ajustarCor(corDeFundo, -30),
      '--cardapio-text': corDaFonte,
      '--cardapio-text-secondary': ajustarOpacidade(corDaFonte, 0.65),
      '--cardapio-accent': botoesCorDeFundo,
      '--cardapio-btn-text': botoesCorDaFonte,
      '--cardapio-card-shadow': fundoClaro ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
      '--cardapio-grupos-bg': gruposCorDeFundo,
      '--cardapio-grupos-text': gruposCorDaFonte,
      '--cardapio-card-bg': cardCorDeFundo,
      '--cardapio-card-text': cardCorDaFonte,
      '--cardapio-cabecalho-bg': cabecalhoCorDeFundo,
      '--cardapio-cabecalho-text': cabecalhoCorDaFonte,
    };

    Object.entries(variaveis).forEach(([prop, valor]) => {
      host.style.setProperty(prop, valor);
    });

    const root = document.documentElement;
    Object.entries(variaveis).forEach(([prop, valor]) => {
      root.style.setProperty(prop, valor);
    });

    // Aplicar o fundo do tema tambem no body (evita vazar a cor do sistema ERP)
    document.body.style.backgroundColor = corDeFundo;
  }

  ngOnDestroy(): void {
    this.pararPollingCobrancaQr();
    if (typeof document !== 'undefined') {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.documentElement.style.scrollbarWidth = '';
      document.body.style.scrollbarWidth = '';
      document.documentElement.classList.remove('cardapio-digital-active');
      document.body.classList.remove('cardapio-digital-active');
      document.body.style.backgroundColor = this.bodyBgOriginal;

      const root = document.documentElement;
      const variaveisCss = [
        '--cardapio-bg', '--cardapio-surface', '--cardapio-surface-hover',
        '--cardapio-border', '--cardapio-text', '--cardapio-text-secondary',
        '--cardapio-accent', '--cardapio-btn-text', '--cardapio-card-shadow',
        '--cardapio-grupos-bg', '--cardapio-grupos-text',
        '--cardapio-card-bg', '--cardapio-card-text',
        '--cardapio-cabecalho-bg', '--cardapio-cabecalho-text',
      ];
      variaveisCss.forEach(prop => root.style.removeProperty(prop));
    }

    this.subs.forEach(s => s.unsubscribe());
    this.intersectionObserver?.disconnect();
    if (this.manualScrollTimeout) {
      clearTimeout(this.manualScrollTimeout);
    }

    // Limpar SEO (Schema.org, Open Graph, PWA, favicon, title, theme-color)
    this.seoService.destroy();
  }
}
